import React, {
  useEffect,
  useState,
  useLayoutEffect,
  useContext,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { Map, GoogleApiWrapper } from 'google-maps-react';
// Importa do build UMD (CJS) em vez do ESM. O webpack 4 do CRA 3.x nao
// consegue resolver named imports do '.mjs' do package (gera erro
// "Can't import named export 'deepEqual' from non EcmaScript module").
// UMD/CJS expoe os mesmos named exports e o webpack consegue interop.
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from '@googlemaps/markerclusterer/dist/index.umd';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ThemeContext } from 'styled-components';
import { icons } from '../../../../assets';
import { Input, Loader, ScrollList, EmptyState } from '../../../../components';
import {
  getStatus,
  getStatusInfo,
  STATUS_PRIMARY,
  isAlarmStatus as isAlarmStatusInfo,
  notifyError,
  notifySuccess,
  useAbortSignal,
  isAbortError,
} from '../../../../utils';
import {
  fetchAllParks,
  fetchParksByZone,
  fetchCustomersByZone,
  getDataDashboard,
  getSearchParquesClientes,
  getParksPendingCommands,
  getLlmStatus,
} from '../../../../services';
import * as s from './styled-pracas-mapa';
import { mapStyles } from '../../../../themes/mapStyles';
import { useDashboardPolling } from './use-dashboard-polling';
import { useActivityNotifications } from './use-activity-notifications';
import {
  getCachedMarkerIcon,
  getParkStatusCounts,
  buildMarkerBadgeSegments,
  getCachedClusterIcon,
  aggregateClusterMachines,
  getMarkerFillColor,
  getCachedBadgePillIcon,
  measureBadgePillWidth,
  badgePillRect,
  badgePillHeight,
} from './marker-helpers';
import MapLegend from './map-legend';
import AlertOverlay from './alert-overlay';
import MarkerDetailPanel from './marker-detail-panel';
import FullscreenLiveTotals from './fullscreen-live-totals';
import FullscreenTopGen from './fullscreen-top-gen';
import ShiftSummaryModal from './shift-summary-modal';
import MapErrorBoundary from './map-error-boundary';

// Threshold de zoom: abaixo disso, esconde os badges (parques aparecem
// so como circulo) e o clusterer agrupa parques proximos. Acima, mostra
// badges e cada marker individual. 9 ~ nivel de cidade no Google Maps.
const BADGE_AND_CLUSTER_MAX_ZOOM = 8;

// Ordem de tentativa do balao de pre-info (pill) na deteccao de colisao:
// preferimos acima; senao abaixo; senao direita; senao esquerda.
const PILL_PLACEMENTS = ['top', 'bottom', 'right', 'left'];

// AABB overlap (retangulos em px de tela).
const rectsOverlap = (a, b) =>
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

// So renderizamos marker de parque que tenha ao menos 1 equipamento
// ATIVO. applianceAmount ja vem do backend como a soma das things
// ativas (maquinas desativadas via toggle nao contam). Parques zerados
// — todas as maquinas desativadas, ou sem cadastro — somem do mapa em
// todos os niveis (empresa/estado/cliente), pois nao ha o que monitorar.
const markerHasEquipment = (m) => Number(m && m.applianceAmount) >= 1;

// Soma tempo-real (geracao UCHA / consumo EBA) dos parques RODANDO de um
// conjunto de markers nativos — usada no pill de soma do cluster. Mesma
// fonte/fallback do badge individual (live throttled + snapshot). Aplica
// Math.abs: UCHA gera com sinal negativo na convencao do backend; exibimos
// modulo positivo igual ao card do dashboard.
const sumClusterRealtime = (childMarkers, realtimeMap, snapshotMap) => {
  let genSum = 0;
  let consSum = 0;
  (childMarkers || []).forEach((m) => {
    if (!m || !m.get) return;
    // Soma a geracao/consumo de qualquer filho que NAO esteja desligado/
    // sem dados — inclui ALARME e ATENCAO (um parque em alarme pode estar
    // gerando). Antes exigia RODANDO agregado e sumia esses da soma do
    // cluster. Parque desligado/sem-comunicacao fica de fora (valor stale).
    const { primary } = getStatusInfo(m.get('statusLabel'));
    if (
      primary === STATUS_PRIMARY.DESLIGADA ||
      primary === STATUS_PRIMARY.SEM_DADOS
    ) {
      return;
    }
    const pid = m.get('parqueId');
    const rt = (realtimeMap && realtimeMap[pid]) || {};
    const snap = (snapshotMap && snapshotMap[pid]) || {};
    const g = Number(rt.onTimeUcha != null ? rt.onTimeUcha : snap.onTimeUcha);
    if (Number.isFinite(g)) genSum += g;
    const c = Number(
      rt.potenciaEba != null ? rt.potenciaEba : snap.potenciaEba,
    );
    if (Number.isFinite(c)) consSum += c;
  });
  return { gen: Math.abs(genSum), cons: Math.abs(consSum) };
};
const filterMarkersWithEquipment = (list) =>
  Array.isArray(list) ? list.filter(markerHasEquipment) : list;

// Assinatura compacta do status por-thing de um parque ('thingId:statusId').
// Usada no diff de markers pra detectar transicao de UMA maquina mesmo
// quando o status AGREGADO do parque nao muda (ex.: 1 das 6 bombas sai de
// sem-dados pra rodando num parque que ja estava OPERANDO). Sem isso o
// setMarkers nao dispararia e o feed de Atividade perderia o evento.
// Module-level pra ser estavel (nao entra em dep de hook).
const thingStatusSig = (m) =>
  ((m && m.appliance) || [])
    .map((app) =>
      ((app && app.thing) || [])
        .map((t) => {
          const st = t && t.status;
          const key = st && (st.id != null ? st.id : st.name);
          return `${t && t.id}:${key != null ? key : '?'}`;
        })
        .join(','),
    )
    .join('|');

// Quanto tempo um alarme recem-detectado fica com o halo vermelho extra
// + diferenciacao visual. Apos isso vira alarme "regular" (igual aos
// outros). 30s e suficiente pro operador notar e nao polui se ficar
// muito tempo.
const FRESH_ALARM_DURATION_MS = 30 * 1000;

// TTL e storage key do ack individual de alertas (botao ✓ no AlertOverlay).
// Module-level pra serem estaveis entre renders e ficarem fora do dep
// array do useEffect que faz cleanup.
const ACK_TTL_MS = 5 * 60 * 1000;
const ACK_STORAGE_KEY = 'cco-acked-alerts';

// Throttle e tamanho max do buffer de historico realtime usado nos
// sparklines do MarkerDetailPanel. 10s entre updates evita inflar
// state com a alta frequencia da subscription onUpdateIOT. 30 pontos
// cobrem ~5min de historico — suficiente pro grafico contextual no
// painel sem pesar a memoria.
const HISTORY_THROTTLE_MS = 10 * 1000;
const HISTORY_MAX_POINTS = 30;

// Util pra tocar um beep sintetizado via Web Audio API sem precisar
// de asset audio. Frequencia 880Hz por 0.4s — alto mas curto, mistura
// de seno e quadrada pra ter presenca sem ser estridente. Mute por
// padrao (operador clica botao Som pra ativar).
let beepAudioContext = null;
const playAlarmBeep = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!beepAudioContext) beepAudioContext = new Ctx();
    if (beepAudioContext.state === 'suspended') beepAudioContext.resume();
    const t0 = beepAudioContext.currentTime;
    // Dois beeps curtos pra chamar atencao sem ser invasivo
    [0, 0.18].forEach((offset) => {
      const osc = beepAudioContext.createOscillator();
      const gain = beepAudioContext.createGain();
      osc.connect(gain);
      gain.connect(beepAudioContext.destination);
      osc.frequency.value = 880;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0, t0 + offset);
      gain.gain.linearRampToValueAtTime(0.25, t0 + offset + 0.02);
      gain.gain.linearRampToValueAtTime(0, t0 + offset + 0.12);
      osc.start(t0 + offset);
      osc.stop(t0 + offset + 0.15);
    });
  } catch {
    // Browser bloqueou (politica de autoplay) ou Web Audio nao existe.
    // Silencia, o toast visual + halo ja sinalizam.
  }
};

// Beep de resolucao: 2 tons ascendentes (660Hz -> 880Hz) — psicologia
// de "subindo / tudo certo", oposto do beep flat de alarme. Mais curto
// e menor volume (0.18 vs 0.25) — confirmacao sutil, nao chama atencao
// urgente como o alarme novo.
const playResolvedBeep = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!beepAudioContext) beepAudioContext = new Ctx();
    if (beepAudioContext.state === 'suspended') beepAudioContext.resume();
    const t0 = beepAudioContext.currentTime;
    [
      { freq: 660, offset: 0 },
      { freq: 880, offset: 0.12 },
    ].forEach(({ freq, offset }) => {
      const osc = beepAudioContext.createOscillator();
      const gain = beepAudioContext.createGain();
      osc.connect(gain);
      gain.connect(beepAudioContext.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0, t0 + offset);
      gain.gain.linearRampToValueAtTime(0.18, t0 + offset + 0.02);
      gain.gain.linearRampToValueAtTime(0, t0 + offset + 0.10);
      osc.start(t0 + offset);
      osc.stop(t0 + offset + 0.13);
    });
  } catch {
    // ignora — toast visual ja sinaliza
  }
};

// Cue de PERDA DE COMUNICACAO (thing -> sem dados). Tom grave descendente
// (520->380Hz) e baixo volume (0.12) — discreto de proposito: comunica
// "perdi contato com uma maquina" sem o peso/urgencia do alarme (que e bem
// mais alto e em sol agudo). Distinto tambem do 'resolvido' (ascendente).
const playCommsLossBeep = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!beepAudioContext) beepAudioContext = new Ctx();
    if (beepAudioContext.state === 'suspended') beepAudioContext.resume();
    const t0 = beepAudioContext.currentTime;
    [
      { freq: 520, offset: 0 },
      { freq: 380, offset: 0.14 },
    ].forEach(({ freq, offset }) => {
      const osc = beepAudioContext.createOscillator();
      const gain = beepAudioContext.createGain();
      osc.connect(gain);
      gain.connect(beepAudioContext.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0, t0 + offset);
      gain.gain.linearRampToValueAtTime(0.12, t0 + offset + 0.02);
      gain.gain.linearRampToValueAtTime(0, t0 + offset + 0.12);
      osc.start(t0 + offset);
      osc.stop(t0 + offset + 0.15);
    });
  } catch {
    // ignora — toast/feed visual ja sinaliza
  }
};

// (isAlarmStatus removido — agora usamos isAlarmStatusInfo importado
// do utils que opera no struct getStatusInfo, sem string-match.)

// Util pra preferencia do operador no localStorage (toggle do som)
const SOUND_PREF_KEY = 'cco:alarm-sound-enabled';
const readSoundPref = () => {
  try {
    return localStorage.getItem(SOUND_PREF_KEY) === '1';
  } catch {
    return false;
  }
};
const writeSoundPref = (enabled) => {
  try {
    localStorage.setItem(SOUND_PREF_KEY, enabled ? '1' : '0');
  } catch {
    // localStorage bloqueado/cheio — silencia
  }
};

// Chave do localStorage pra views salvas (bookmarks) — operador pode
// salvar mais de uma view nomeada e voltar a elas com 1 click. Array
// de { id, name, lat, lng, zoom }.
const SAVED_VIEWS_STORAGE_KEY = 'cco:dashboard-saved-views';

const readSavedViews = () => {
  try {
    const raw = localStorage.getItem(SAVED_VIEWS_STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const writeSavedViews = (views) => {
  try {
    localStorage.setItem(SAVED_VIEWS_STORAGE_KEY, JSON.stringify(views));
  } catch {
    // localStorage bloqueado/cheio — silencia
  }
};

const filtros = [
  'alarme/bloqueio',
  'atenção',
  'rodando',
  'desligada',
  'sem dados',
];

const PracasMapa = ({
  google,
  markers,
  setMarkers,
  selectedClient,
  setSelectedClient,
  // embedded=true: modo "incorporado" pra reuso fora do /dashboard
  // (ex.: /dashboard/customer). Diferencas:
  //   - Sidebar de zonas/clientes ESCONDIDA
  //   - Fetch interno de parques DESLIGADO (markers vem 100% do prop)
  //   - Polling DESLIGADO
  //   - Fullscreen/bookmarks/sound continuam funcionando
  // O parent (ex.: cliente.js) controla totalmente os markers.
  embedded = false,
  // ccoLayout=true: layout X/Twitter no /dashboard — Container vira
  // grid 3-col (filtros 380 | mapa 1fr | alertas 360), AlertOverlay
  // sai do absolute e vira sibling do MapContainer. Funcionalidades
  // 100% iguais — so a topologia DOM/CSS muda.
  ccoLayout = false,
}) => {
  const { title, colors } = useContext(ThemeContext);
  const companyId = useSelector((state) => state.usuario.user.company.id);
  const zonas = useSelector((state) => state.parque.zones);
  // Agregados "AGORA" (geracao/consumo + vazao) — mesma fonte do card
  // VisaoGeralUchasEbas do dashboard. Usados pelos totais flutuantes
  // sobrepostos no rodape do mapa em TELA CHEIA (modo TV no CCO).
  const uchasOverview = useSelector((state) => state.primeiroNivel.uchas);
  const ebasOverview = useSelector((state) => state.primeiroNivel.ebas);
  // Snapshot por parque (onTimeUcha, vazaoUcha, potenciaEba, vazaoEba)
  // alimentado pelo saga PRIMEIRO/ABRE_CONEXAO_THINGS. Usado no tooltip
  // do marker para mostrar energia gerada (UCHA) ou vazao atual (EBA).
  const parqueRealtime = useSelector(
    (state) => state.primeiroNivel.parqueRealtime || {}
  );
  // Versao THROTTLED (~10s) do parqueRealtime, usada SO pelos badges/tooltip
  // dos markers do mapa. A subscription atualiza parqueRealtime ~1x/s; sem
  // throttle, o useEffect dos markers re-rodava recalculando badge + SVG de
  // TODOS os ~100 parques a cada segundo (maior custo de CPU na TV 24/7).
  // O painel de detalhe e os sparklines continuam lendo parqueRealtime ao
  // vivo (selectors proprios) — so o badge/tooltip do mapa cai pra 10s.
  const parqueRealtimeRef = useRef(parqueRealtime);
  parqueRealtimeRef.current = parqueRealtime;
  const [throttledRealtime, setThrottledRealtime] = useState(parqueRealtime);
  // Ref lido dentro do renderer do clusterer (criado 1x, fecha sobre o ref).
  // Usado pra somar geracao/consumo dos parques RODANDO de cada cluster.
  const clusterRealtimeRef = useRef(throttledRealtime);
  clusterRealtimeRef.current = throttledRealtime;
  useEffect(() => {
    const id = setInterval(() => {
      setThrottledRealtime(parqueRealtimeRef.current);
    }, 10000);
    return () => clearInterval(id);
  }, []);
  // Estado de saude das subscriptions GraphQL ('ok' default, 'failed'
  // quando alguma quebrou). Alimenta o indicador visual no
  // UpdatedAgo / sobreposicao de aviso de conexao.
  const subscriptionHealth = useSelector(
    (state) => state.primeiroNivel.subscriptionHealth || 'ok'
  );
  // Banner proeminente de subscription caida — debounced. A subscription
  // reconecta sozinha com backoff (1s->2s->...->30s); blips curtos nao
  // devem piscar o banner. So mostramos apos 'failed' persistir 6s, o
  // que filtra reconexoes rapidas e avisa o operador apenas quando o
  // tempo real esta de fato comprometido.
  const [showSubFailedBanner, setShowSubFailedBanner] = useState(false);
  useEffect(() => {
    if (subscriptionHealth !== 'failed') {
      setShowSubFailedBanner(false);
      return undefined;
    }
    const t = setTimeout(() => setShowSubFailedBanner(true), 6000);
    return () => clearTimeout(t);
  }, [subscriptionHealth]);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [search, setSearch] = useState('');
  const [selectedZona, setSelectedZona] = useState({});
  // Filter agora e mutavel pra suportar os chips de filtro top-center
  // do mapa. Operador pode toggle individualmente cada status pra focar
  // em alertas / esconder sem-dados / etc.
  const [filterStatus, setFilterStatus] = useState(filtros);
  const toggleFilter = useCallback((filter) => {
    setFilterStatus((current) =>
      current.includes(filter)
        ? current.filter((f) => f !== filter)
        : [...current, filter],
    );
  }, []);
  // Restaura todos os status — usado pelo empty state quando o filtro
  // atual escondeu todos os parques.
  const resetFilters = useCallback(() => setFilterStatus(filtros), []);
  const [clientes, setClientes] = useState([]);
  const [zonasFiltradas, setZonasFiltradas] = useState(false);
  const [clientesFiltrados, setClientesFiltrados] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState({});

  // ─── URL state sync ─────────────────────────────────────────────
  // Persiste `state` (zona id) e `customer` (client id) no querystring
  // pra:
  //   - F5 nao perde o contexto
  //   - operador compartilha link "olha esse cliente aqui" com URL
  //   - voltar do navegador respeita stack
  //
  // Restore: roda 1x quando `zonas`/`clientes` chegam do redux. Como
  // dependem das listas pra resolver o id em objeto, aguardamos os
  // dados antes de aplicar.
  const urlRestoredRef = useRef({ state: false, customer: false });

  useEffect(() => {
    // Restore state (zona) — depende de zonas[] estar carregado
    if (urlRestoredRef.current.state) return;
    if (!zonas || !zonas.length) return;
    const params = new URLSearchParams(location.search);
    const stateId = params.get('state');
    if (stateId) {
      const z = zonas.find((zz) => String(zz.id) === String(stateId));
      if (z) setSelectedZona(z);
    }
    urlRestoredRef.current.state = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zonas]);

  useEffect(() => {
    // Restore customer — depende de clientes[] (que so vem apos a zona)
    if (urlRestoredRef.current.customer) return;
    if (!clientes || !clientes.length) return;
    const params = new URLSearchParams(location.search);
    const customerId = params.get('customer');
    if (customerId) {
      const c = clientes.find((cc) => String(cc.id) === String(customerId));
      if (c) setSelectedClient(c);
    }
    urlRestoredRef.current.customer = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientes]);

  // Sync state/customer SELECTIONS pro URL via history.replace (sem
  // criar entry no stack — refresh idempotente).
  useEffect(() => {
    // Espera o restore completar antes de comecar a escrever no URL
    // (senao apagamos o param recebido).
    if (!urlRestoredRef.current.state) return;
    const params = new URLSearchParams(location.search);
    const before = params.toString();

    if (selectedZona && selectedZona.id) {
      params.set('state', String(selectedZona.id));
    } else {
      params.delete('state');
      params.delete('customer'); // sem state nao faz sentido manter cliente
    }
    if (selectedClient && selectedClient.id) {
      params.set('customer', String(selectedClient.id));
    } else {
      params.delete('customer');
    }
    const after = params.toString();
    if (before !== after) {
      history.replace({
        pathname: location.pathname,
        search: after ? `?${after}` : '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedZona.id, selectedClient.id]);

  // Em embedded mode, sincroniza selectedMarker local com o
  // parqueSelecionado do Redux — assim quando o consumer (ex.:
  // AplicacoesCliente) dispatcha SET_PARQUE_SELECIONADO ao clicar
  // num card, o marker correspondente vira selectedMarker e ganha
  // o destaque visual no mapa (border lightblue + halo). Cobre
  // o sentido card → marker da selecao bidirecional.
  const parqueSelecionadoRedux = useSelector(
    (state) => state.segundoNivel?.parqueSelecionado
  );
  useEffect(() => {
    if (!embedded) return;
    const targetId = parqueSelecionadoRedux && parqueSelecionadoRedux.id;
    if (!targetId) {
      setSelectedMarker({});
      return;
    }
    const currentId =
      selectedMarker && selectedMarker.props && selectedMarker.props.position
        ? selectedMarker.props.position.id
        : null;
    if (currentId === targetId) return; // ja sincronizado
    // Acha o marker correspondente no array de parques (pode nao
    // existir ainda se Redux foi setado antes dos markers chegarem)
    const parque = (markers || []).find((m) => m.id === targetId);
    if (parque) {
      setSelectedMarker({ props: { position: parque }, name: parque.name });
    }
    // selectedMarker omitido das deps — usado so pra check de
    // identidade dentro do effect (evita re-trigger). markers/
    // parqueSelecionadoRedux/embedded sao os triggers reais.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parqueSelecionadoRedux, markers, embedded]);
  const [, setWidth] = useState(window.innerWidth);
  // Em embedded (/dashboard/customer) NAO ha fetch interno — o parent
  // passa os markers via prop. Comecar loadingMap=true causa flicker do
  // Loader na montagem antes do useEffect rodar e setar false; o pior
  // caso era ficar preso no Loader se a useEffect nao re-disparasse por
  // alguma razao (dependencias instaveis vindo do parent). Iniciando
  // como !embedded eliminamos a janela onde isso podia acontecer.
  const [loadingMap, setLoadingMap] = useState(!embedded);
  const mapRef = useRef(null);
  // Refs do fit-de-contexto em 2 estagios — declarados aqui no topo
  // pra evitar TDZ em handlers (handleApplyView, etc) que os referenciam.
  // Logica completa esta no useEffect abaixo (estagio 1 + estagio 2).
  const pendingFitRef = useRef(true); // true inicial pro primeiro fit no mount
  const lastFittedMarkersRef = useRef(null);

  // ─── Hover-pan no ScrollList CCO ────────────────────────────────
  // Operador passa o mouse num estado → debounce 250ms → mapa
  // centraliza naquela posicao (panTo suave, mantem zoom atual).
  // Operador sai do hover sem entrar em outro estado → debounce 250ms
  // → mapa volta pra visao inicial (fitBounds em todos os markers).
  //
  // Sequencia de hover entre estados (A → B): leave A cancela enter
  // timer e agenda leave timer; antes do leave timer disparar,
  // enter B cancela ele e agenda enter timer. So volta ao inicial
  // quando realmente nenhum item esta hovered por 250ms+.
  //
  // Hooks declarados aqui no topo pra atender rules-of-hooks (antes
  // de qualquer early return condicional do componente).
  const hoverEnterTimerRef = useRef(null);
  const hoverLeaveTimerRef = useRef(null);
  // initialViewRef guarda o center+zoom do mapa quando o operador
  // ainda nao tocou em nada (apos o primeiro fitBounds dos markers).
  // Capturado on-demand no primeiro hover enter — assim pegamos a
  // visao "settled" depois do fitBounds inicial, nao o center cru.
  const initialViewRef = useRef(null);

  // Hint de hover-pan: mostrado pro operador novato pra ensinar que
  // passar o mouse num estado centraliza o mapa. Dismissa no primeiro
  // hover OU click explicito no X. Persiste em localStorage pra nao
  // re-aparecer em sessoes futuras.
  const HOVER_HINT_KEY = 'cco-hover-pan-hint-seen';
  const [showHoverHint, setShowHoverHint] = useState(() => {
    try {
      return !localStorage.getItem(HOVER_HINT_KEY);
    } catch {
      return false;
    }
  });
  const dismissHoverHint = useCallback(() => {
    setShowHoverHint(false);
    try {
      localStorage.setItem(HOVER_HINT_KEY, '1');
    } catch {
      // ignora (modo privacidade / SSR / etc)
    }
  }, []);

  const handleZonaHoverEnter = useCallback(
    (zona) => {
      // Primeira interacao dismissa o hint — operador aprendeu
      if (showHoverHint) dismissHoverHint();
      if (!ccoLayout || !mapRef.current || !zona) return;
      const { lat, lng } = zona;
      if (lat == null || lng == null) return;
      // Captura snapshot da visao inicial uma unica vez — apenas no
      // PRIMEIRO hover, quando o mapa ja settled apos fitBounds.
      if (!initialViewRef.current) {
        const c = mapRef.current.getCenter();
        if (c) {
          initialViewRef.current = {
            lat: c.lat(),
            lng: c.lng(),
            zoom: mapRef.current.getZoom(),
          };
        }
      }
      // Cancela QUALQUER timer pendente (enter ou leave)
      if (hoverEnterTimerRef.current) {
        clearTimeout(hoverEnterTimerRef.current);
      }
      if (hoverLeaveTimerRef.current) {
        clearTimeout(hoverLeaveTimerRef.current);
        hoverLeaveTimerRef.current = null;
      }
      hoverEnterTimerRef.current = setTimeout(() => {
        if (!mapRef.current) return;
        mapRef.current.panTo({ lat, lng });
        hoverEnterTimerRef.current = null;
      }, 250);
    },
    [ccoLayout, showHoverHint, dismissHoverHint],
  );

  const handleZonaHoverLeave = useCallback(() => {
    // Cancela enter timer (operador saiu antes de comitar)
    if (hoverEnterTimerRef.current) {
      clearTimeout(hoverEnterTimerRef.current);
      hoverEnterTimerRef.current = null;
    }
    // Agenda restore pra visao inicial. Se outro hoverEnter chegar
    // antes do timeout, ele cancela este leave timer (acima na
    // funcao enter).
    if (hoverLeaveTimerRef.current) {
      clearTimeout(hoverLeaveTimerRef.current);
    }
    hoverLeaveTimerRef.current = setTimeout(() => {
      if (!mapRef.current || !initialViewRef.current) return;
      const { lat, lng, zoom } = initialViewRef.current;
      mapRef.current.panTo({ lat, lng });
      // Restaura zoom tambem — se operador deu zoom em algum estado
      // via panTo (que mantem zoom), o zoom inicial pode ter ficado
      // diferente. Restaurar volta a visao panoramica.
      if (zoom != null && mapRef.current.getZoom() !== zoom) {
        mapRef.current.setZoom(zoom);
      }
      hoverLeaveTimerRef.current = null;
    }, 250);
  }, []);

  // Cleanup dos timers no unmount pra evitar setTimeout pendurado.
  useEffect(
    () => () => {
      if (hoverEnterTimerRef.current) {
        clearTimeout(hoverEnterTimerRef.current);
      }
      if (hoverLeaveTimerRef.current) {
        clearTimeout(hoverLeaveTimerRef.current);
      }
    },
    [],
  );
  // mapZoom alimenta a logica de show/hide do badge e poderia ser usado
  // em outros lugares no futuro (ex.: maxZoom do clusterer). Iniciado
  // como null pra evitar renderizar markers antes do mapa montar.
  const [mapZoom, setMapZoom] = useState(null);
  // Refs imperativos pros markers nativos do google.maps + clusterer.
  // Substituiram o <Marker> de google-maps-react: a lib nao integra com
  // MarkerClusterer (precisamos passar instancias diretas pra ele).
  // window.Map e o Map nativo do JavaScript — sem o prefixo, o
  // identificador 'Map' resolve pro componente importado de
  // google-maps-react acima (colisao de nome).
  const nativeMarkersRef = useRef(new window.Map()); // parqueId -> google.maps.Marker
  const clustererRef = useRef(null);
  // Markers SEPARADOS do balao de pre-info (pill), fora do clusterer, pra
  // poder reposicionar (anti-colisao). parqueId -> google.maps.Marker.
  const badgeMarkersRef = useRef(new window.Map());
  // Histerese: ultimo lado escolhido por parque. Preferimos manter o lado
  // anterior se ainda estiver livre — evita o balao "pular" de lado a cada
  // pan/zoom (efeito nervoso).
  const badgePlacementRef = useRef(new window.Map());
  // Parque sob hover (no marker do mapa OU no item da lista de alertas) —
  // realce bidirecional mapa<->lista. null = nenhum.
  const [hoveredParqueId, setHoveredParqueId] = useState(null);
  // Marker atualmente em "bounce" (realce de hover) pra restaurar no proximo.
  const bouncingRef = useRef(null);
  // Parques com comando pendente (shadow desired aguardando) em alguma
  // maquina. Endpoint leve separado (cache 30s no back) — polling proprio.
  // Set de parqueId; o marker ganha um indicador de "comando aguardando".
  const [pendingCommandIds, setPendingCommandIds] = useState(
    () => new window.Set(),
  );

  // === Detector de alarmes novos ===
  // freshAlarms guarda { [parqueId]: timestamp } pra parques que ENTRARAM
  // em alarme nos ultimos 30s. Usado pra pintar halo vermelho + toast +
  // beep. Persiste em state pra triggerar re-render dos icones.
  const [freshAlarms, setFreshAlarms] = useState({});

  // Feed de Atividade: notificacoes transitorias de transicao por-maquina
  // (comecou a rodar / desligou / sem dados / recuperou). Derivado do diff
  // de markers entre ticks; auto-expira. Renderizado no AlertOverlay.
  // Cue sonoro discreto SO pra perda de comunicacao (thing -> sem dados),
  // respeitando o toggle de som. Demais transicoes (rodou/desligou) nao
  // tocam pra nao virar poluicao sonora; alarme novo ja tem beep proprio.
  // Ref pro soundEnabled lido dentro do callback sem recriar o hook.
  const soundEnabledRef = useRef(false);
  const handleNewActivity = useCallback((fresh) => {
    if (!soundEnabledRef.current) return;
    if (fresh.some((e) => e.kind === 'sem-dados')) playCommsLossBeep();
  }, []);
  const {
    activity,
    history: activityHistory,
    clearHistory: clearActivityHistory,
  } = useActivityNotifications(markers, handleNewActivity);

  // Acked alerts: operador marcou "ja vi" via ✓ no AlertOverlay. Esconde
  // o item da lista por 5 min sem afetar a logica de alerta real (marker
  // no mapa continua vermelho, statusMap continua reportando). E uma
  // marcacao client-side pra reduzir ruido visual quando ha alarmes que
  // o operador ja registrou mas nao tem acao imediata.
  //
  // Persiste em localStorage com TTL — sobrevive F5 mas expira sozinho.
  const readAckedAlerts = () => {
    try {
      const raw = localStorage.getItem(ACK_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      const now = Date.now();
      const fresh = {};
      Object.entries(parsed).forEach(([id, ts]) => {
        if (typeof ts === 'number' && now - ts < ACK_TTL_MS) {
          fresh[id] = ts;
        }
      });
      return fresh;
    } catch {
      return {};
    }
  };
  const [ackedAlerts, setAckedAlerts] = useState(readAckedAlerts);

  // Limpa entradas expiradas em loop discreto (1min). Sem isso o
  // localStorage poderia inflar com ids antigos qdo o operador acka
  // muitos parks ao longo do turno.
  useEffect(() => {
    const id = setInterval(() => {
      setAckedAlerts((prev) => {
        const now = Date.now();
        const next = {};
        let changed = false;
        Object.entries(prev).forEach(([k, ts]) => {
          if (now - ts < ACK_TTL_MS) next[k] = ts;
          else changed = true;
        });
        return changed ? next : prev;
      });
    }, 60000);
    return () => clearInterval(id);
  }, []);

  const handleAckAlert = useCallback((markerId) => {
    setAckedAlerts((prev) => {
      const next = { ...prev, [markerId]: Date.now() };
      try {
        localStorage.setItem(ACK_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignora
      }
      return next;
    });
  }, []);
  // previousStatusesRef guarda { [parqueId]: 'rodando'|'alarme/bloqueio'|... }
  // do tick anterior. Compara com current pra detectar transicoes (status
  // virou alarme).
  const previousStatusesRef = useRef({});
  // Toggle de som on/off (localStorage). Default off — operador clica
  // o botao Som pra ativar. Algumas politicas de browser tambem
  // bloqueiam Web Audio antes de interacao do usuario.
  const [soundEnabled, setSoundEnabled] = useState(readSoundPref);
  // Mantem o ref em dia pro callback de cue sonoro (handleNewActivity) ler
  // o valor atual sem recriar o hook de atividade.
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  // Views salvas (bookmarks) — operador pode salvar a view atual com
  // nome e voltar 1-click depois. Persistido em localStorage.
  const [savedViews, setSavedViews] = useState(readSavedViews);
  const [bookmarksOpen, setBookmarksOpen] = useState(false);
  // Modal "Resumo do turno" (briefing IA da janela recente).
  const [shiftSummaryOpen, setShiftSummaryOpen] = useState(false);
  // Pergunta pre-montada pro copiloto (triagem de alarme clicado). null =
  // modal abre no modo briefing normal.
  const [shiftSummaryQuestion, setShiftSummaryQuestion] = useState(null);
  // Triagem IA de um alerta: monta a pergunta com maquina/causa/parque e
  // abre o copiloto ja perguntando.
  const handleAskAi = useCallback((marker) => {
    const ta = (marker && marker.topAlarm) || {};
    const machine = ta.thingName || ta.applianceName || marker.name;
    const cause = ta.variable || 'alarme ativo';
    setShiftSummaryQuestion(
      `Triagem de alarme: máquina ${machine} (parque ${marker.name}) com a causa "${cause}". ` +
        'Qual a recorrência recente dessa causa nessa máquina e o que o operador deve verificar primeiro?',
    );
    setShiftSummaryOpen(true);
  }, []);
  // O botao so aparece se o backend tem ANTHROPIC_API_KEY (feature-flag
  // via /report/llm-status). Default false: sem flag, sem botao.
  const [llmEnabled, setLlmEnabled] = useState(false);
  useEffect(() => {
    let active = true;
    getLlmStatus()
      .then((res) => {
        const body = (res && res.data) || res || {};
        const flag =
          body.llmEnabled != null
            ? body.llmEnabled
            : body.data && body.data.llmEnabled;
        if (active) setLlmEnabled(!!flag);
      })
      .catch(() => {
        // endpoint ausente/erro -> botao fica oculto (comportamento seguro)
      });
    return () => {
      active = false;
    };
  }, []);
  const handleSaveCurrentView = useCallback(() => {
    if (!mapRef.current) return;
    const center = mapRef.current.getCenter();
    const zoom = mapRef.current.getZoom();
    if (!center || typeof zoom !== 'number') return;
    // Pedimos um nome — prompt e simples e funciona em TV/desktop.
    // Se cancelar, sai. Se vazio, gera 'View N'.
    // eslint-disable-next-line no-alert
    let name = window.prompt('Nome desta visão:');
    if (name === null) return;
    name = name.trim();
    setSavedViews((current) => {
      const fallbackName =
        name || `Visão ${current.length + 1}`;
      const next = [
        ...current,
        {
          id: `view-${Date.now()}`,
          name: fallbackName,
          lat: center.lat(),
          lng: center.lng(),
          zoom,
        },
      ];
      writeSavedViews(next);
      return next;
    });
  }, []);
  const handleApplyView = useCallback((view) => {
    if (!mapRef.current) return;
    mapRef.current.setCenter({ lat: view.lat, lng: view.lng });
    mapRef.current.setZoom(view.zoom);
    setBookmarksOpen(false);
    // Cancela qualquer fit pendente — operador aplicou view manual,
    // nao queremos sobrescrever com fitBounds dos markers.
    pendingFitRef.current = false;
  }, []);
  const handleDeleteView = useCallback((viewId) => {
    setSavedViews((current) => {
      const next = current.filter((v) => v.id !== viewId);
      writeSavedViews(next);
      return next;
    });
  }, []);
  const toggleSound = useCallback(() => {
    setSoundEnabled((prev) => {
      const next = !prev;
      writeSoundPref(next);
      // Toque um beep pra confirmar que tem som
      if (next) playAlarmBeep();
      return next;
    });
  }, []);

  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const { getSignal } = useAbortSignal();

  // Declarado aqui no topo (em vez de junto ao toggleFullscreen abaixo)
  // porque o polling adaptativo depende deste estado e o hook precisa
  // ser chamado antes do JSX. Logica do toggle vem mais pra baixo.
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (search.length) {
        getSearchParquesClientes(search.trim())
          .then(({ data }) => {
            setZonasFiltradas(data.zones);
            setClientesFiltrados(data.customers);
          })
          .catch(() => {
            notifyError('Não foi possível buscar os parques');
          });
      } else {
        setZonasFiltradas(false);
        setClientesFiltrados(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [search]);

  const applyDashboardSummaryData = useCallback(
    (data) => {
      dispatch({
        type: 'DASHBOARD/SET_THINGS_DATA',
        data,
      });
      dispatch({
        type: 'PRIMEIRO/SET_VALORES_UCHAS_EBAS',
        uchas: data.ucha,
        ebas: data.eba,
      });
    },
    [dispatch]
  );

  const getDashboardSummaryContext = useCallback(() => {
    if (selectedClient.id) {
      return {
        id: selectedClient.id,
        type: 'customer',
      };
    }

    if (selectedZona.id) {
      return {
        id: selectedZona.id,
        type: 'zone',
      };
    }

    return {
      id: companyId,
      type: 'company',
    };
  }, [companyId, selectedClient.id, selectedZona.id]);

  const refreshDashboardSummary = useCallback(async () => {
    const { id, type } = getDashboardSummaryContext();
    try {
      const { data } = await getDataDashboard(id, type, getSignal());
      if (!mountedRef.current) return;
      applyDashboardSummaryData(data);
    } catch (err) {
      if (isAbortError(err)) return; // navegou pra fora, silencia
      throw err;
    }
  }, [applyDashboardSummaryData, getDashboardSummaryContext, getSignal]);

  const getMarkers = useCallback(
    (idCliente) => {
      fetchParksByZone([selectedZona.id], idCliente)
        .then((resp) => {
          if (!mountedRef.current) return;
          setMarkers(filterMarkersWithEquipment(resp.data));
        })
        .catch(() => {
          if (!mountedRef.current) return;
          notifyError('Erro ao buscar os parques');
        });
    },
    [selectedZona.id, setMarkers]
  );


  // Comparacao shallow dos campos visuais dos markers pra evitar
  // re-render do Google Maps quando o polling retorna dados identicos.
  // Re-render dos <Marker> e caro (re-cria DOM SVG/canvas), entao so
  // troca o array se algo que afeta o icone/tooltip mudou.
  const haveMarkersChanged = useCallback((prev, next) => {
    if (prev.length !== next.length) return true;
    for (let i = 0; i < next.length; i += 1) {
      const a = prev[i];
      const b = next[i];
      // topAlarm muda quando a variavel critica troca ou seu valor cruza
      // outro threshold — comparamos por variable+value+since pra detectar
      // tanto "outra variavel virou top" quanto "valor da mesma variavel
      // mudou no polling tick". Sem isso, AlertItem/MarkerDetailPanel
      // ficavam stale ate o operador clicar fora e voltar.
      const aTop = a?.topAlarm;
      const bTop = b?.topAlarm;
      const topChanged =
        (aTop?.variable || null) !== (bTop?.variable || null) ||
        (aTop?.value ?? null) !== (bTop?.value ?? null) ||
        (aTop?.since ?? null) !== (bTop?.since ?? null) ||
        (aTop?.level || null) !== (bTop?.level || null);
      if (
        !a ||
        a.id !== b.id ||
        Number(a.lat) !== Number(b.lat) ||
        Number(a.lng) !== Number(b.lng) ||
        a.statusMap?.status !== b.statusMap?.status ||
        a.statusMap?.isRangeWarning !== b.statusMap?.isRangeWarning ||
        a.applianceAmount !== b.applianceAmount ||
        (a.applianceErrors?.length || 0) !==
          (b.applianceErrors?.length || 0) ||
        topChanged ||
        thingStatusSig(a) !== thingStatusSig(b)
      ) {
        return true;
      }
    }
    return false;
  }, []);

  // Dedup window: o useEffect de mount tambem fetcha markers (mesma
  // chamada) e o useDashboardPolling dispara refreshAll imediato no
  // mount. Sem dedup, 2 fetches identicos em < 100ms. Ref guarda
  // timestamp do ultimo fetch — skipa se < 2s.
  const lastMarkersFetchAtRef = useRef(0);
  const MARKERS_FETCH_DEDUP_MS = 2000;

  // Refresh dos markers (status, applianceErrors, etc) via fetch.
  // Polling chama isto a cada tick pra TV nao ficar com markers stale.
  // Diff-merge: so dispara setMarkers se algo visualmente mudou.
  const refreshMarkers = useCallback(async () => {
    const now = Date.now();
    if (now - lastMarkersFetchAtRef.current < MARKERS_FETCH_DEDUP_MS) {
      // Acabou de fetchar (mount useEffect ou tick anterior) — pula.
      return;
    }
    lastMarkersFetchAtRef.current = now;
    try {
      const { data } = selectedZona.id
        ? await fetchParksByZone(
            [selectedZona.id],
            selectedClient.id || undefined
          )
        : await fetchAllParks();
      if (!mountedRef.current) return;
      const filtered = filterMarkersWithEquipment(data);
      setMarkers((prev) =>
        haveMarkersChanged(prev || [], filtered || []) ? filtered : prev
      );
    } catch {
      // Silencioso no polling — toast inicial ja cobriu falha de rede,
      // nao queremos spamar o operador a cada ciclo se backend cair.
    }
  }, [
    selectedZona.id,
    selectedClient.id,
    setMarkers,
    haveMarkersChanged,
  ]);

  // [lastUpdate] timestamp do ultimo polling bem-sucedido. Alimenta o
  // indicador "Atualizado ha Xs" pra operador na TV ter confirmacao
  // visual de que o dado e fresco, nao congelado por erro de rede.
  const [lastUpdate, setLastUpdate] = useState(null);

  // Refresh combinado: summary (alimenta AlertOverlay + visao geral) +
  // markers (cor/numero do mapa). Promise.allSettled pra falha de um
  // nao matar o outro.
  const refreshAll = useCallback(async () => {
    await Promise.allSettled([refreshDashboardSummary(), refreshMarkers()]);
    if (mountedRef.current) setLastUpdate(Date.now());
  }, [refreshDashboardSummary, refreshMarkers]);

  // Polling adaptativo: em fullscreen (modo TV no CCO) aperta pra 1min
  // e janela 24h porque a operacao roda a noite toda e ninguem ta ali
  // pra clicar refresh. Workstation normal: 5min e janela 06h-20h
  // (default do hook) — operador interage clicando, polling agressivo
  // seria desperdicio de backend.
  const pollingOptions = useMemo(
    () => ({
      intervalMs: isFullscreen ? 60 * 1000 : 5 * 60 * 1000,
      window24h: isFullscreen,
    }),
    [isFullscreen],
  );
  // Polling: em embedded, pulamos refresh — markers vem 100% do prop,
  // o parent (cliente.js) gerencia atualizacao.
  useDashboardPolling(embedded ? () => {} : refreshAll, pollingOptions);

  useEffect(() => {
    // Em embedded, o parent fornece markers — bypassa o fetch interno
    // de parques (fetchAllParks / fetchCustomersByZone). selectedZona
    // continua usavel pra outras coisas (filter chips, etc.).
    if (embedded) {
      setLoadingMap(false);
      return undefined;
    }
    let mounted = true;

    // F5 com ?state=<zona> na URL: espera o restore da zona (effect acima)
    // antes de fetchar. Sem isso, este effect roda 1x com selectedZona={}
    // e dispara fetchAllParks (TODOS os parques da empresa — query pesada),
    // que e descartado logo apos o restore re-disparar o effect (cleanup
    // marca mounted=false). Era trabalho jogado fora que deixava o F5
    // lento. Lemos window.location (nao o hook) pra nao virar dep do
    // effect. urlRestoredRef.state vira true quando zonas chegam (mesmo se
    // a zona da URL nao existir), entao isto nao trava o carregamento.
    const hasUnrestoredZona =
      !!new URLSearchParams(window.location.search).get('state') &&
      !urlRestoredRef.current.state;
    if (hasUnrestoredZona) {
      return undefined;
    }

    if (selectedZona.id) {
      fetchCustomersByZone(selectedZona.id, companyId)
        .then(({ data }) => {
          if (!mounted) return;
          setClientes(data);
          lastMarkersFetchAtRef.current = Date.now();
          getMarkers();
        })
        .catch(() => {
          if (!mounted) return;
          notifyError('Não foi possível buscar os clientes');
        })
        .finally(() => {
          // CRITICO: este branch precisa limpar loadingMap igual ao else.
          // Sem isso, no F5 com ?state=<zona> na URL o mapa ficava preso
          // no Loader: o restore da zona (via URL) re-dispara o effect, o
          // cleanup do run anterior marca mounted=false, e o fetchAllParks
          // pendente cai no early-return — ninguem setava loadingMap=false.
          if (!mounted) return;
          setLoadingMap(false);
        });
    } else {
      fetchAllParks()
        .then(({ data }) => {
          if (!mounted) return;
          // Marca timestamp pra dedup do refreshMarkers do polling, que
          // dispara imediato ao montar e iria fetchar a mesma coisa.
          lastMarkersFetchAtRef.current = Date.now();
          setMarkers(filterMarkersWithEquipment(data));
        })
        .catch(() => {
          if (!mounted) return;
          notifyError('Não foi possível buscar os parques');
        })
        .finally(() => {
          if (!mounted) return;
          setLoadingMap(false);
        });
    }
    return () => {
      mounted = false;
    };
  }, [companyId, getMarkers, selectedZona.id, setMarkers, embedded]);

  // Pre-warm do cache de icones: assim que markers chegam, gera os
  // icones sem badge / nao-selecionado pra todos. Custo upfront ~5ms
  // pra 100 parques, mas elimina o jank do primeiro render do
  // useEffect imperativo dos markers (onde cada novo google.maps.Marker
  // pega o icone via cache hit em vez de buildMarkerIcon completo).
  useEffect(() => {
    if (!google || !markers || !markers.length) return;
    markers.forEach((m) => {
      if (!m.statusMap) return;
      const statusLabel = getStatus(
        m.statusMap.status,
        m.statusMap.isRangeWarning,
      );
      getCachedMarkerIcon(
        google,
        statusLabel,
        m.applianceAmount,
        [],
        false,
        false,
        false,
        getParkStatusCounts(m),
      );
    });
  }, [google, markers]);

  const updateSize = () => {
    if (window.innerWidth > 1425) {
      setWidth(window.innerWidth - 950);
    } else {
      setWidth(window.innerWidth - 550);
    }
  };

  useLayoutEffect(() => {
    updateSize();
    // Debounce: o resize browser dispara dezenas de eventos durante o
    // drag da janela (cada pixel). Sem debounce, dezenas de setState
    // por segundo. 120ms agrupa em um ate o drag parar. Trailing-edge
    // (executa apos o ultimo evento) — garante valor final correto.
    let resizeTimer = null;
    const debouncedUpdateSize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateSize, 120);
    };
    window.addEventListener('resize', debouncedUpdateSize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', debouncedUpdateSize);
    };
  }, []);

  // Fullscreen do mapa pra exibir em TV no centro de operacao. Coloca o
  // MapContainer inteiro em fullscreen (mantem AlertOverlay + MapLegend
  // visiveis, esconde a side-list de zonas/clientes). Sincroniza estado
  // com fullscreenchange pra cobrir saida via ESC ou via API do browser.
  // (isFullscreen e declarado no topo do componente porque o polling
  // adaptativo depende dele.)
  const toggleFullscreen = useCallback(() => {
    const el = document.getElementById('dashboard-map-container');
    if (!el) return;
    const fsElement =
      document.fullscreenElement || document.webkitFullscreenElement;
    if (fsElement) {
      const exit =
        document.exitFullscreen?.bind(document) ||
        document.webkitExitFullscreen?.bind(document);
      exit?.();
    } else {
      const request =
        el.requestFullscreen?.bind(el) ||
        el.webkitRequestFullscreen?.bind(el);
      request?.();
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      const fsElement =
        document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(
        !!fsElement && fsElement.id === 'dashboard-map-container'
      );
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  const handleMarkerClick = (props, _marker, name) => {
    setSelectedMarker({ props, name });
    // Em embedded mode, sincroniza com Redux parqueSelecionado pra que
    // o consumer (ex.: AplicacoesCliente do /dashboard/customer) possa
    // reagir destacando o card correspondente. Toggle: clicar no mesmo
    // marker deselectiona.
    if (embedded) {
      const clickedId = props && props.position && props.position.id;
      const currentId = selectedMarker?.props?.position?.id;
      const isSameMarker = clickedId && currentId === clickedId;
      dispatch({
        type: 'SEGUNDO/SET_PARQUE_SELECIONADO',
        parqueSelecionado: isSameMarker ? {} : props.position,
      });
      if (isSameMarker) setSelectedMarker({});
    }
  };

  // Fecha o painel de detalhe do parque, voltando pra exibicao do
  // AlertOverlay (lista "Em alerta agora"). Vias de fechar: back-button
  // do painel, ESC, ou clicar em outro marker (substitui o conteudo).
  // NAO fechamos no click do mapa pra nao conflitar com double-click
  // zoom do Google Maps.
  const closeDetailPanel = useCallback(() => {
    setSelectedMarker({});
  }, []);

  // Cluster popup (spiderfy alternativo): quando o operador clica num
  // cluster que ja esta no zoom maximo (parques tao proximos que zoom
  // in nao separa), mostra um menu listando os parques agrupados em
  // vez de fazer mais zoom. Click num item do menu seleciona o marker
  // correspondente (abre painel de detalhe).
  // Declarado AQUI no topo (em vez de junto aos outros handlers) porque
  // o ESC handler abaixo depende dele — temporal dead zone se ficar
  // mais embaixo.
  const [clusterPopup, setClusterPopup] = useState(null);
  // Ref pro setter pra evitar stale closure no callback do
  // MarkerClusterer (configurado uma vez na criacao).
  const clusterPopupSetterRef = useRef(setClusterPopup);
  clusterPopupSetterRef.current = setClusterPopup;

  // ESC fecha o painel de detalhe ou o cluster popup, na ordem
  // (popup tem precedencia se ambos abertos — sai do nivel atual).
  useEffect(() => {
    if (!selectedMarker.props && !clusterPopup) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (clusterPopup) setClusterPopup(null);
        else closeDetailPanel();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedMarker.props, clusterPopup, closeDetailPanel]);

  // Id do marker selecionado pra destacar no mapa (anel branco + circulo
  // maior). Atalho seguro contra optional chain.
  const selectedMarkerId = selectedMarker?.props?.position?.id;

  // Ref pra callback de click sempre atual sem precisar re-attach o
  // listener no marker. Solucao classica pra closures stale.
  const handleMarkerClickRef = useRef(handleMarkerClick);
  handleMarkerClickRef.current = handleMarkerClick;

  // Contador de alertas ativos — usado no back-button do painel pra
  // informar o operador quantos alertas tem aguardando ao voltar
  // (ex.: "‹ Em alerta agora (8)"). Match a logica do AlertOverlay
  // (alarme/bloqueio/atencao). Memoizado pra nao recalcular toda
  // render.
  const alertCount = useMemo(() => {
    if (!markers || !markers.length) return 0;
    return markers.reduce((acc, m) => {
      const info = getStatusInfo(
        m.statusMap.status,
        m.statusMap.isRangeWarning,
      );
      // Conta como alerta: alarme primario OU atencao (flag).
      const isAlert = isAlarmStatusInfo(info) || info.hasAttention;
      return acc + (isAlert ? 1 : 0);
    }, 0);
  }, [markers]);

  // Tick a cada 10s so pra atualizar o texto "ha Xs" do indicador.
  // Nao dispara fetch, so re-renderiza o span. Pulamos quando lastUpdate
  // ainda nao foi setado (primeiro polling em curso).
  const [, setNowTick] = useState(0);
  useEffect(() => {
    if (!lastUpdate) return undefined;
    const interval = setInterval(() => setNowTick((t) => t + 1), 10000);
    return () => clearInterval(interval);
  }, [lastUpdate]);

  // Texto humanizado pro indicador. <10s -> "agora", senao "ha Xs" ou
  // "ha Xmin". $stale quando passou 2x o intervalo de polling (dado
  // suspeito de estar congelado por falha de rede).
  const updatedAgoInfo = useMemo(() => {
    if (!lastUpdate) return { label: 'sincronizando...', stale: false };
    const ageMs = Date.now() - lastUpdate;
    const ageSec = Math.floor(ageMs / 1000);
    const stale = ageMs > pollingOptions.intervalMs * 2;
    if (ageSec < 10) return { label: 'agora', stale };
    if (ageSec < 60) return { label: `há ${ageSec}s`, stale };
    return { label: `há ${Math.floor(ageSec / 60)}min`, stale };
    // setNowTick acima dispara este recompute a cada 10s
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUpdate, pollingOptions.intervalMs]);

  // Monta o tooltip native do marker (HTML title attribute) com energia
  // gerada (UCHA) e vazao atual (EBA) somando todas as things do parque.
  // Title e text-only (renderizado pelo OS) — sem emoji, label textual
  // ('Pot.' / 'Vazao') que e mais profissional e legivel.
  // useCallback dependendo de parqueRealtime — referencia estavel entre
  // renders quando parqueRealtime nao muda, permite usar como dep do
  // useEffect dos markers sem disparar re-run desnecessario.
  const buildMarkerTooltip = useCallback(
    (marker) => {
      const realtime = throttledRealtime[marker.id];
      if (!realtime) return marker.name;

      const parts = [marker.name];

      if (Number.isFinite(realtime.onTimeUcha) && realtime.onTimeUcha !== 0) {
        // Math.abs: UCHA gera com sinal negativo (convencao do backend);
        // exibimos o modulo, igual ao balao/painel (senao vinha "-257 kW").
        const gerando = Math.abs(realtime.onTimeUcha).toLocaleString('pt-BR', {
          maximumFractionDigits: 2,
        });
        parts.push(`${gerando} kW (UCHA)`);
      }

      if (Number.isFinite(realtime.vazaoEba) && realtime.vazaoEba !== 0) {
        const vazao = realtime.vazaoEba.toLocaleString('pt-BR', {
          maximumFractionDigits: 2,
        });
        parts.push(`Vazao ${vazao} l/s (EBA)`);
      }

      return parts.join(' — ');
    },
    [throttledRealtime],
  );

  const filteredMarkers = useMemo(
    () =>
      markers.filter((m) =>
        filterStatus.find((filter) =>
          getStatus(m.statusMap.status, m.statusMap.isRangeWarning).includes(
            filter
          )
        )
      ),
    [markers, filterStatus]
  );
  // Ref pra lookup do parque dentro de listeners (spiderfy) sem stale.
  const filteredMarkersRef = useRef(filteredMarkers);
  filteredMarkersRef.current = filteredMarkers;

  // Detector de transicao pra alarme: compara markers atuais com o
  // snapshot do tick anterior. Cada parque que ESTAVA OK e agora esta
  // em alarme/bloqueio dispara: toast vermelho + beep (se som on) +
  // entrada em freshAlarms (halo vermelho pulsante).
  //
  // 'previous[id] !== undefined' filtra o mount inicial — nao consideramos
  // todos os alarmes como "novos" no primeiro carregamento (operador
  // nao foi avisado, ja existiam). So transicoes futuras contam.
  useEffect(() => {
    if (!markers || !markers.length) return;
    const previous = previousStatusesRef.current;
    const current = {};
    const newAlarms = {};
    const newAlarmNames = [];
    // Resolucoes: alarmes que ESTAVAM ativos e nao estao mais. Ajuda
    // o operador a perceber que problema foi mitigado (silencio era
    // ambiguo: pode ter resolvido OU pode ter perdido a notificacao).
    const resolvedNames = [];

    markers.forEach((m) => {
      // Armazena info struct no ref pra evitar reparsing de string em
      // cada tick. Comparacao alarme<->ok via isAlarmStatusInfo no
      // struct — sem string-match.
      const info = getStatusInfo(
        m.statusMap.status,
        m.statusMap.isRangeWarning,
      );
      current[m.id] = info;
      const prevInfo = previous[m.id];
      if (prevInfo === undefined) return; // mount inicial, nao conta
      const wasAlarm = isAlarmStatusInfo(prevInfo);
      const isAlarm = isAlarmStatusInfo(info);
      if (!wasAlarm && isAlarm) {
        // Transicao OK -> alarme
        newAlarms[m.id] = Date.now();
        newAlarmNames.push(m.name);
      } else if (wasAlarm && !isAlarm) {
        // Transicao alarme -> OK (resolvido)
        resolvedNames.push(m.name);
      }
    });

    previousStatusesRef.current = current;

    if (newAlarmNames.length) {
      // Toast por parque (max 3 pra nao saturar a tela)
      newAlarmNames.slice(0, 3).forEach((name) => {
        notifyError(`${name} entrou em alarme`);
      });
      if (newAlarmNames.length > 3) {
        notifyError(`+${newAlarmNames.length - 3} parques em alarme`);
      }
      if (soundEnabled) playAlarmBeep();
      setFreshAlarms((prev) => ({ ...prev, ...newAlarms }));
    }

    if (resolvedNames.length) {
      // Toast verde por resolucao (max 3 igual aos alarmes).
      resolvedNames.slice(0, 3).forEach((name) => {
        notifySuccess(`${name} voltou ao normal`);
      });
      if (resolvedNames.length > 3) {
        notifySuccess(`+${resolvedNames.length - 3} parques voltaram ao normal`);
      }
      // Beep de resolucao (tons ascendentes 660->880Hz) — so toca se NAO
      // houve alarme novo no mesmo tick. Alarme novo tem prioridade
      // sonora: operador precisa AGIR > saber que algo se resolveu.
      // Sem isso, dois beeps competiriam e operador nao entenderia qual
      // foi qual.
      if (soundEnabled && newAlarmNames.length === 0) {
        playResolvedBeep();
      }
    }
    // soundEnabled em deps pra capturar valor atualizado sem stale
    // closure. notifyError e import top-level, estavel.
  }, [markers, soundEnabled]);

  // === Historico curto de parqueRealtime pra sparkline no painel ===
  // Buffer rolling com ate 30 pontos (5min a 10s/ponto). Usado pelo
  // MarkerDetailPanel pra renderizar mini-grafico da metrica selecionada.
  // Throttle interno de 10s por parque pra nao inflar state com cada
  // update da subscription. Constantes no top-level do modulo
  // (HISTORY_THROTTLE_MS, HISTORY_MAX_POINTS) — fora do componente
  // pra serem estaveis entre renders e nao precisarem de dep.
  const [realtimeHistory, setRealtimeHistory] = useState({});
  // Espelho em ref pra leitura na passada de baloes (sparkline) sem virar
  // dep do useCallback (evita re-rodar a colisao a cada tick de historico).
  const realtimeHistoryRef = useRef(realtimeHistory);
  realtimeHistoryRef.current = realtimeHistory;
  const lastHistoryUpdateRef = useRef({});
  useEffect(() => {
    if (!parqueRealtime || typeof parqueRealtime !== 'object') return;
    const now = Date.now();
    setRealtimeHistory((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.entries(parqueRealtime).forEach(([id, data]) => {
        if (!data) return;
        const lastUpdate = lastHistoryUpdateRef.current[id] || 0;
        if (now - lastUpdate < HISTORY_THROTTLE_MS) return;
        lastHistoryUpdateRef.current[id] = now;
        const history = next[id] || [];
        next[id] = [
          ...history.slice(-(HISTORY_MAX_POINTS - 1)),
          { ...data, ts: now },
        ];
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [parqueRealtime]);

  // === Snapshot anterior do parqueRealtime pra calcular trend ===
  // realtimeSnapshotRef guarda um snapshot de ~60s atras pra cada
  // parque, usado pelos badges pra mostrar seta de tendencia (▲▼).
  // Usamos ref + setInterval em vez de state porque o snapshot nao
  // precisa triggerar re-render — o useEffect imperativo dos markers
  // pega o valor atual quando re-roda por outro motivo (polling, etc).
  const realtimeSnapshotRef = useRef({});
  useEffect(() => {
    const interval = setInterval(() => {
      realtimeSnapshotRef.current = { ...parqueRealtime };
    }, 60 * 1000);
    // Captura snapshot inicial imediato pra setar baseline (sem isso,
    // primeiro tick de trend usaria 0 e mostraria up pra tudo).
    if (Object.keys(realtimeSnapshotRef.current).length === 0) {
      realtimeSnapshotRef.current = { ...parqueRealtime };
    }
    return () => clearInterval(interval);
    // parqueRealtime omitido das deps porque o snapshot e propositalmente
    // atrasado — re-criar interval a cada update zeraria o atraso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup dos freshAlarms expirados (>30s). Roda enquanto houver
  // freshAlarms ativos. Quando esvaziar, para o interval.
  useEffect(() => {
    if (!Object.keys(freshAlarms).length) return undefined;
    const interval = setInterval(() => {
      setFreshAlarms((prev) => {
        const now = Date.now();
        let changed = false;
        const next = {};
        Object.entries(prev).forEach(([id, ts]) => {
          if (now - ts < FRESH_ALARM_DURATION_MS) {
            next[id] = ts;
          } else {
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [freshAlarms]);

  // Gerenciamento imperativo dos markers nativos + MarkerClusterer. Antes
  // usavamos <Marker> de google-maps-react direto no JSX, mas a lib nao
  // expoe a instancia google.maps.Marker pro clusterer agrupar. Agora
  // criamos manualmente com diff por id:
  //
  //   1. Inicializa MarkerClusterer uma vez quando o map ta pronto.
  //   2. Pra cada filteredMarker:
  //      - Se ja existe: atualiza icone/title/zIndex se mudaram.
  //      - Se nao existe: cria google.maps.Marker novo + click listener
  //        + adiciona ao clusterer.
  //   3. Markers que sairam (id nao esta no novo filteredMarkers):
  //      remove do clusterer + setMap(null) pra apagar do mapa.
  //
  // Badge so aparece em zoom alto (>= BADGE_AND_CLUSTER_MAX_ZOOM + 1) —
  // em zoom baixo, badges sobrepoem; clusterer ja agrupa parques nessa
  // faixa entao nao tem ponto em mostrar info individual mesmo.
  useEffect(() => {
    if (!mapRef.current || !google || !google.maps || mapZoom === null) {
      return undefined;
    }

    // Inicializa clusterer na primeira vez. Algoritmo SuperCluster
    // (KDBush) e o padrao do googlemaps/markerclusterer — eficiente
    // pra centenas de markers. maxZoom: alem desse zoom, nao agrupa
    // (parques aparecem individualmente). radius: distancia em pixels
    // pra considerar markers proximos no mesmo cluster.
    if (!clustererRef.current) {
      clustererRef.current = new MarkerClusterer({
        map: mapRef.current,
        algorithm: new SuperClusterAlgorithm({
          maxZoom: BADGE_AND_CLUSTER_MAX_ZOOM,
          radius: 80,
        }),
        // Click no cluster:
        // - Se zoom >= MAX_ZOOM_CLUSTER: parques tao proximos que nem
        //   mais zoom separa. Abre popup com lista dos parques pra
        //   selecao individual (spiderfy alternativo).
        // - Caso contrario: faz fitBounds nos CHILDREN do cluster (em
        //   vez do zoom-step generico do defaultOnClusterClickHandler).
        //   Garante que todos os parques agrupados ficam visiveis no
        //   viewport, com padding right maior pra nao ficar atras do
        //   AlertOverlay (240+12 = 252px).
        onClusterClick: (event, cluster, map) => {
          const currentZoom = map.getZoom();

          // Caso especial: ja em zoom max do clusterer. Mais zoom nao
          // separa parques. Spiderfy: espalha as pracas em leque ao redor
          // do centro pra selecao individual (fallback p/ popup-lista se
          // a projecao nao estiver disponivel).
          if (currentZoom >= BADGE_AND_CLUSTER_MAX_ZOOM) {
            spiderfyClusterRef.current(cluster, map);
            return;
          }

          // Fecha o painel de detalhe (limpa selectedMarker) — sem
          // isso o useEffect de pan-to-marker do marker selecionado
          // poderia disputar com a navegacao do cluster.
          setSelectedMarker({});

          // Target zoom: +3 niveis a partir do atual, cap em 14
          // (city level). Centramos no icon do cluster (cluster.position).
          const targetZoom = Math.min(currentZoom + 3, 14);
          map.setCenter(cluster.position);
          map.setZoom(targetZoom);
        },
        renderer: {
          render: ({ count, position, markers: clusterMarkers }) => {
            // Numero do cluster = SOMA DE MAQUINAS dos parques agrupados
            // (consistente com o marker individual, que mostra maquinas).
            // O pie tambem e ponderado por maquinas — antes contava
            // PARQUES e o operador lia o agrupamento como soma errada.
            const { counts, totalMachines } =
              aggregateClusterMachines(clusterMarkers);
            // Soma tempo-real dos parques RODANDO do cluster: geracao (UCHA)
            // + consumo (EBA). Exibe no pill do cluster pra visualizar o
            // total mesmo agrupado (zoom out). O effect abaixo mantem o pill
            // atualizado in-place sem re-clusterizar (evita flicker).
            const clusterSums = sumClusterRealtime(
              clusterMarkers,
              clusterRealtimeRef.current,
              realtimeSnapshotRef.current,
            );
            // Tooltip rico: maquinas + parques + breakdown por status.
            const tooltipParts = [
              `${totalMachines} máquina${totalMachines === 1 ? '' : 's'} em ${count} parque${count === 1 ? '' : 's'}`,
            ];
            if (counts['alarme/bloqueio'])
              tooltipParts.push(`${counts['alarme/bloqueio']} em alarme`);
            if (counts.atenção)
              tooltipParts.push(`${counts.atenção} em atenção`);
            if (counts.rodando) tooltipParts.push(`${counts.rodando} rodando`);
            return new google.maps.Marker({
              position,
              icon: getCachedClusterIcon(
                google,
                counts,
                totalMachines,
                clusterSums,
              ),
              // zIndex alto pra clusters renderizarem por cima de
              // markers soltos da mesma area durante a transicao zoom.
              zIndex: 999 + count,
              title: tooltipParts.join(' — '),
            });
          },
        },
      });

      // Reposiciona os baloes (anti-colisao) sempre que o clusterer
      // termina de (re)agrupar — timing correto: cl.clusters ja refletem
      // o zoom/pan atual. Usa ref pra pegar a versao mais recente da
      // funcao sem reattach. Registrado 1x (dentro do if de init).
      clustererRef.current.addListener('clusteringend', () => {
        if (!mountedRef.current) return;
        // Re-agrupou (pan/zoom): fecha qualquer leque de spiderfy aberto.
        if (clearSpiderfyRef.current) clearSpiderfyRef.current();
        if (updateBadgePlacementRef.current) updateBadgePlacementRef.current();
      });
    }

    const existing = nativeMarkersRef.current;
    const wantIds = new Set();
    const toAdd = [];
    const toRemove = [];

    filteredMarkers.forEach((parque) => {
      wantIds.add(parque.id);
      const statusLabel = getStatus(
        parque.statusMap.status,
        parque.statusMap.isRangeWarning,
      );
      // O balao de tempo real (geracao UCHA / potencia EBA) NAO entra mais
      // no icone do circulo: virou um marker proprio (pill) reposicionavel
      // pra evitar sobreposicao — ver updateBadgePlacement abaixo. Aqui o
      // circulo fica limpo (segments []).
      const isSelected = parque.id === selectedMarkerId;
      const isFreshAlarm = !!freshAlarms[parque.id];
      const hasPendingCommand = pendingCommandIds.has(parque.id);
      const icon = getCachedMarkerIcon(
        google,
        statusLabel,
        parque.applianceAmount,
        [],
        isSelected,
        isFreshAlarm,
        hasPendingCommand,
        getParkStatusCounts(parque),
      );
      const title = buildMarkerTooltip(parque);

      let marker = existing.get(parque.id);
      if (marker) {
        // Update so se realmente mudou — setIcon/setTitle/setZIndex
        // sao no-ops se o mesmo valor, entao seguro chamar sempre,
        // mas evitamos custo desnecessario comparando referencias.
        if (marker.getIcon() !== icon) marker.setIcon(icon);
        if (marker.getTitle() !== title) marker.setTitle(title);
        const currentPosition = marker.getPosition();
        if (
          !currentPosition ||
          currentPosition.lat() !== Number(parque.lat) ||
          currentPosition.lng() !== Number(parque.lng)
        ) {
          marker.setPosition({
            lat: Number(parque.lat),
            lng: Number(parque.lng),
          });
        }
        const targetZ = isSelected ? 1000 : null;
        if (marker.getZIndex() !== targetZ) {
          marker.setZIndex(targetZ);
        }
        marker.set('statusLabel', statusLabel);
        marker.set('applianceAmount', Number(parque.applianceAmount) || 1);
      } else {
        marker = new google.maps.Marker({
          position: { lat: parque.lat, lng: parque.lng },
          title,
          icon,
          zIndex: isSelected ? 1000 : undefined,
        });
        marker.set('parqueId', parque.id);
        marker.set('statusLabel', statusLabel);
        marker.set('applianceAmount', Number(parque.applianceAmount) || 1);
        // Click listener usa ref pra callback atual (evita stale closure
        // sem precisar reattach quando filteredMarkers re-renderiza).
        // Reconstruimos `props` no formato que selectedMarker espera
        // (mesmo shape da antiga API do google-maps-react).
        marker.addListener('click', () => {
          handleMarkerClickRef.current(
            { position: parque },
            marker,
            parque.name,
          );
        });
        // Hover: realce bidirecional mapa<->lista de alertas.
        marker.addListener('mouseover', () => setHoveredParqueId(parque.id));
        marker.addListener('mouseout', () => setHoveredParqueId(null));
        existing.set(parque.id, marker);
        toAdd.push(marker);
      }
    });

    // Markers que sairam do filtro (zona mudou, ou polling removeu)
    existing.forEach((marker, id) => {
      if (!wantIds.has(id)) {
        toRemove.push(marker);
        existing.delete(id);
      }
    });

    if (toRemove.length) {
      clustererRef.current.removeMarkers(toRemove);
      toRemove.forEach((m) => m.setMap(null));
    }
    if (toAdd.length) {
      clustererRef.current.addMarkers(toAdd);
    }

    return undefined;
  }, [
    google,
    filteredMarkers,
    selectedMarkerId,
    mapZoom,
    buildMarkerTooltip,
    freshAlarms,
    pendingCommandIds,
  ]);

  // Atualiza o pill de soma (geracao/consumo) dos clusters JA renderizados
  // quando o realtime (throttled ~10s) muda — IN-PLACE via setIcon no marker
  // de cada cluster, sem re-clusterizar. (A 1a tentativa fazia removeMarkers+
  // addMarkers pra forcar re-render, mas isso piscava/sumia os markers a cada
  // tick.) clusterer.render() sozinho e no-op quando zoom/markers nao mudam,
  // por isso vamos direto nos cluster.marker existentes. Clusters de 1 so
  // parque mostram o marker individual (badge cuidado pelo effect acima),
  // entao pulamos count<=1.
  useEffect(() => {
    const cl = clustererRef.current;
    if (!cl || !google || !google.maps || !Array.isArray(cl.clusters)) return;
    cl.clusters.forEach((cluster) => {
      if (!cluster || cluster.count <= 1) return;
      const cm = cluster.marker;
      const childMarkers = cluster.markers || [];
      if (!cm || typeof cm.setIcon !== 'function' || !childMarkers.length) {
        return;
      }
      // MESMA agregacao do renderer (maquinas, nao parques) — divergir aqui
      // faria o icone alternar entre as duas contagens a cada tick de 10s.
      const { counts, totalMachines } =
        aggregateClusterMachines(childMarkers);
      const sums = sumClusterRealtime(
        childMarkers,
        clusterRealtimeRef.current,
        realtimeSnapshotRef.current,
      );
      const icon = getCachedClusterIcon(google, counts, totalMachines, sums);
      // getCachedClusterIcon devolve a MESMA ref quando a chave (counts+
      // soma arredondada) nao muda -> setIcon vira no-op, sem flicker.
      if (icon && cm.getIcon() !== icon) cm.setIcon(icon);
    });
  }, [throttledRealtime, mapZoom, google]);

  // === Anti-colisao do balao de pre-info (pill) ===
  // O balao virou marker proprio (badgeMarkersRef), fora do clusterer, pra
  // poder ser reposicionado. Esta passada projeta cada cluster/marker pra
  // pixel da tela, monta a lista de obstaculos e posiciona o balao de cada
  // parque RODANDO mostrado individualmente no 1o lado livre (greedy:
  // top -> bottom -> right -> left), priorizando os de maior potencia.
  // Roda no 'clusteringend' do clusterer (fim de pan/zoom/reagrupamento) e
  // quando dados/selecao/zoom mudam.
  const updateBadgePlacement = useCallback(() => {
    const cl = clustererRef.current;
    const map = mapRef.current;
    if (!cl || !map || !google || !google.maps) return;
    const proj =
      typeof cl.getProjection === 'function' ? cl.getProjection() : null;
    if (!proj) return;
    const clusters = Array.isArray(cl.clusters) ? cl.clusters : [];

    const toPx = (latLng) => {
      try {
        return proj.fromLatLngToContainerPixel(latLng);
      } catch (e) {
        return null;
      }
    };

    // 1. Obstaculos (circulos individuais + clusters) e parques mostrados
    //    individualmente (count===1).
    const obstacles = [];
    const individualIds = new window.Set();
    clusters.forEach((cluster) => {
      if (!cluster || !cluster.position) return;
      const p = toPx(cluster.position);
      if (!p) return;
      if (cluster.count > 1) {
        // Cluster: circulo ~56 + pill de soma abaixo. Caixa generosa.
        obstacles.push({ x: p.x - 30, y: p.y - 30, w: 60, h: 110 });
      } else {
        const pid = cluster.marker && cluster.marker.get('parqueId');
        if (pid != null) individualIds.add(pid);
        obstacles.push({ x: p.x - 24, y: p.y - 24, w: 48, h: 48 });
      }
    });

    // 2. Candidatos: parque mostrado individualmente + RODANDO + com balao.
    const parkById = new window.Map(filteredMarkers.map((pq) => [pq.id, pq]));
    const candidates = [];
    individualIds.forEach((pid) => {
      const parque = parkById.get(pid);
      if (!parque) return;
      const { primary } = getStatusInfo(
        parque.statusMap.status,
        parque.statusMap.isRangeWarning,
      );
      // Mostra o balao pra qualquer parque que NAO esteja desligado/sem
      // dados — inclui ALARME e ATENCAO. Antes exigia RODANDO agregado, o
      // que escondia a geracao de um parque com uma turbina gerando mas
      // OUTRA maquina em alarme (o status agregado vira alarme). O guard
      // de segments.length abaixo garante que so aparece com valor real.
      if (
        primary === STATUS_PRIMARY.DESLIGADA ||
        primary === STATUS_PRIMARY.SEM_DADOS
      ) {
        return;
      }
      const segments = buildMarkerBadgeSegments(
        throttledRealtime[pid],
        realtimeSnapshotRef.current[pid],
      );
      if (!segments.length) return;
      const p = toPx(new google.maps.LatLng(parque.lat, parque.lng));
      if (!p) return;
      const rt =
        throttledRealtime[pid] || realtimeSnapshotRef.current[pid] || {};
      const power =
        Math.abs(Number(rt.onTimeUcha) || 0) +
        Math.abs(Number(rt.potenciaEba) || 0);
      candidates.push({ pid, parque, segments, p, power });
    });
    // Maior potencia escolhe lugar primeiro (prioridade visual).
    candidates.sort((a, b) => b.power - a.power);

    // Densidade adaptativa: em zoom afastado, muitos parques isolados
    // espalhados poluem. Limita os baloes visiveis aos de MAIOR potencia
    // (cap por zoom); o resto so reaparece ao dar zoom. Sem cluster pra
    // agrupa-los, isso evita a tela virar um mar de baloes.
    let cap = Infinity;
    if (mapZoom != null) {
      if (mapZoom <= 5) cap = 8;
      else if (mapZoom <= 7) cap = 16;
    }
    const visible = candidates.slice(0, cap);

    // Limites da viewport pra clamp (nao deixar o balao sair da tela). Em
    // tela cheia reservamos a faixa direita do dock de alertas (~392px).
    const div = map.getDiv();
    const vw = (div && div.offsetWidth) || 0;
    const vh = (div && div.offsetHeight) || 0;
    const rightReserve = isFullscreen ? 392 : 0;
    // Em tela cheia tambem reservamos a faixa do dock "Top Geracao" (esq).
    const leftReserve = isFullscreen ? 352 : 0;
    const inView = (r) =>
      vw > 0 &&
      vh > 0 &&
      r.x >= 4 + leftReserve &&
      r.y >= 4 &&
      r.x + r.w <= vw - 4 - rightReserve &&
      r.y + r.h <= vh - 4;

    // Stream tempo-real caiu? Esmaece os baloes (dado possivelmente velho).
    const streamStale = subscriptionHealth === 'failed';

    // 3. Greedy: 1o lado livre (com histerese e clamp). Acumula os rects.
    const placed = obstacles.slice();
    const liveIds = new window.Set();
    visible.forEach(({ pid, parque, segments, p }) => {
      const rectW = measureBadgePillWidth(segments);
      // Sparkline: tendencia recente (gen+consumo) das ultimas leituras.
      const hist = realtimeHistoryRef.current[pid] || [];
      const spark =
        hist.length >= 3
          ? hist
              .slice(-12)
              .map(
                (d) =>
                  Math.abs(Number(d.onTimeUcha) || 0) +
                  Math.abs(Number(d.potenciaEba) || 0),
              )
          : null;
      const hasSpark = !!(spark && spark.length >= 3);
      const rectH = badgePillHeight(hasSpark);
      // Histerese: tenta primeiro o lado usado da ultima vez.
      const last = badgePlacementRef.current.get(pid);
      const order = last
        ? [last].concat(PILL_PLACEMENTS.filter((x) => x !== last))
        : PILL_PLACEMENTS;
      // 1a passada: livre E dentro da viewport. 2a: livre (mesmo fora).
      const pick = (requireView) => {
        for (let i = 0; i < order.length; i += 1) {
          const r = badgePillRect(order[i], p.x, p.y, rectW, rectH);
          if (
            !placed.some((o) => rectsOverlap(o, r)) &&
            (!requireView || inView(r))
          ) {
            return { pl: order[i], r };
          }
        }
        return null;
      };
      let result = pick(true) || pick(false);
      if (!result) {
        const pl = last || 'top';
        result = { pl, r: badgePillRect(pl, p.x, p.y, rectW, rectH) };
      }
      placed.push(result.r);
      badgePlacementRef.current.set(pid, result.pl);

      const icon = getCachedBadgePillIcon(google, segments, result.pl, {
        stale: streamStale,
        spark,
      });
      if (!icon) return;
      const selected = pid === selectedMarkerId;
      const z = selected ? 1001 : 900;
      let bm = badgeMarkersRef.current.get(pid);
      if (!bm) {
        bm = new google.maps.Marker({
          position: { lat: parque.lat, lng: parque.lng },
          icon,
          // Clicavel: clicar no balao seleciona o parque (mesmo handler
          // do circulo). UX: o numero E o balao levam ao detalhe.
          clickable: true,
          zIndex: z,
        });
        bm.set('parqueId', pid);
        bm.addListener('click', () => {
          handleMarkerClickRef.current(
            { position: parque },
            nativeMarkersRef.current.get(pid) || bm,
            parque.name,
          );
        });
        bm.addListener('mouseover', () => setHoveredParqueId(pid));
        bm.addListener('mouseout', () => setHoveredParqueId(null));
        badgeMarkersRef.current.set(pid, bm);
      } else {
        if (bm.getIcon() !== icon) bm.setIcon(icon);
        if (bm.getZIndex() !== z) bm.setZIndex(z);
        const currentPosition = bm.getPosition();
        if (
          !currentPosition ||
          currentPosition.lat() !== Number(parque.lat) ||
          currentPosition.lng() !== Number(parque.lng)
        ) {
          bm.setPosition({
            lat: Number(parque.lat),
            lng: Number(parque.lng),
          });
        }
      }
      if (bm.getMap() !== map) bm.setMap(map);
      liveIds.add(pid);
    });

    // 4. Esconde/remove baloes inativos neste frame (parque agrupado, parou
    //    de rodar, ou saiu do filtro).
    badgeMarkersRef.current.forEach((bm, pid) => {
      if (!liveIds.has(pid)) {
        if (bm.getMap()) bm.setMap(null);
        if (!parkById.has(pid)) {
          badgeMarkersRef.current.delete(pid);
          badgePlacementRef.current.delete(pid);
        }
      }
    });
  }, [
    google,
    filteredMarkers,
    throttledRealtime,
    selectedMarkerId,
    mapZoom,
    isFullscreen,
    subscriptionHealth,
  ]);

  // Ref pra ultima versao da funcao — usada pelo listener 'clusteringend'
  // (registrado 1x na criacao do clusterer) sem precisar reattach.
  const updateBadgePlacementRef = useRef(updateBadgePlacement);
  updateBadgePlacementRef.current = updateBadgePlacement;

  // Reposiciona quando dados/selecao/zoom mudam (alem do clusteringend).
  useEffect(() => {
    updateBadgePlacement();
  }, [updateBadgePlacement, mapZoom]);

  // Polling proprio (30s) dos parques com comando pendente. Endpoint leve
  // (cache 30s no back) — separado do polling pesado do mapa pra nao
  // degradar. Best-effort: erro e silencioso (indicador some).
  useEffect(() => {
    let active = true;
    const fetchPending = async () => {
      try {
        const res = await getParksPendingCommands();
        const ids =
          (res && res.data && res.data.parkIds) ||
          (res && res.parkIds) ||
          [];
        if (active && mountedRef.current) {
          setPendingCommandIds(new window.Set(ids));
        }
      } catch (e) {
        // indicador e best-effort; ignora falha
      }
    };
    fetchPending();
    const interval = setInterval(fetchPending, 30 * 1000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  // Realce de hover (bidirecional mapa<->lista): o marker do parque sob
  // hover faz bounce e sobe pra frente. Restaura o anterior ao mudar.
  useEffect(() => {
    if (!google || !google.maps) return undefined;
    const prev = bouncingRef.current;
    if (prev && prev.marker) {
      prev.marker.setAnimation(null);
      prev.marker.setZIndex(prev.z);
    }
    bouncingRef.current = null;
    if (hoveredParqueId != null) {
      const m = nativeMarkersRef.current.get(hoveredParqueId);
      if (m && m.getMap()) {
        bouncingRef.current = { marker: m, z: m.getZIndex() };
        m.setAnimation(google.maps.Animation.BOUNCE);
        m.setZIndex(1002);
      }
    }
    return undefined;
  }, [hoveredParqueId, google]);

  // === Spiderfy: ao clicar num cluster no zoom maximo (parques tao
  // proximos que mais zoom nao separa), espalha as pracas em leque ao redor
  // do centro, com linhas-guia, pra selecao individual. Markers e linhas
  // temporarios (fora do clusterer); limpa ao clicar fora / pan / zoom.
  const spiderfyRef = useRef({
    markers: [],
    lines: [],
    clickListener: null,
    zoomListener: null,
  });
  const clearSpiderfy = useCallback(() => {
    const s = spiderfyRef.current;
    s.markers.forEach((m) => m.setMap(null));
    s.lines.forEach((l) => l.setMap(null));
    s.markers = [];
    s.lines = [];
    if (s.clickListener) {
      s.clickListener.remove();
      s.clickListener = null;
    }
    if (s.zoomListener) {
      s.zoomListener.remove();
      s.zoomListener = null;
    }
  }, []);
  const spiderfyCluster = useCallback(
    (cluster, map) => {
      clearSpiderfy();
      const cl = clustererRef.current;
      const proj = cl && cl.getProjection ? cl.getProjection() : null;
      const childMarkers = (cluster && cluster.markers) || [];
      // Sem projecao: fallback pro popup-lista (nao da pra espalhar sem px).
      if (!proj || !google || !google.maps || childMarkers.length === 0) {
        const parqueIds = childMarkers
          .map((m) => (m.get ? m.get('parqueId') : undefined))
          .filter((id) => id != null);
        clusterPopupSetterRef.current({
          position: cluster.position,
          parqueIds,
        });
        return;
      }
      const centerPx = proj.fromLatLngToContainerPixel(cluster.position);
      const n = childMarkers.length;
      // Raio do leque: cresce com a contagem pra os markers nao colarem.
      const R = Math.max(64, Math.min(150, (n * 46) / (2 * Math.PI)));
      childMarkers.forEach((child, i) => {
        const angle = (2 * Math.PI * i) / n - Math.PI / 2;
        const x = centerPx.x + Math.cos(angle) * R;
        const y = centerPx.y + Math.sin(angle) * R;
        const latLng = proj.fromContainerPixelToLatLng(
          new google.maps.Point(x, y),
        );
        if (!latLng) return;
        const pid = child.get ? child.get('parqueId') : undefined;
        const line = new google.maps.Polyline({
          path: [cluster.position, latLng],
          strokeColor: '#9aa0a6',
          strokeOpacity: 0.7,
          strokeWeight: 1.5,
          map,
          zIndex: 1100,
        });
        spiderfyRef.current.lines.push(line);
        const sm = new google.maps.Marker({
          position: latLng,
          icon: child.getIcon(),
          title: child.getTitle(),
          zIndex: 1101,
          map,
        });
        sm.addListener('click', () => {
          const parque = filteredMarkersRef.current.find(
            (pq) => pq.id === pid,
          );
          clearSpiderfy();
          if (parque) {
            handleMarkerClickRef.current(
              { position: parque },
              sm,
              parque.name,
            );
          }
        });
        spiderfyRef.current.markers.push(sm);
      });
      // Fecha o leque ao clicar no mapa (fora) ou ao dar zoom.
      spiderfyRef.current.clickListener = map.addListener('click', () =>
        clearSpiderfy(),
      );
      spiderfyRef.current.zoomListener = map.addListener('zoom_changed', () =>
        clearSpiderfy(),
      );
    },
    [google, clearSpiderfy],
  );
  const spiderfyClusterRef = useRef(spiderfyCluster);
  spiderfyClusterRef.current = spiderfyCluster;
  const clearSpiderfyRef = useRef(clearSpiderfy);
  clearSpiderfyRef.current = clearSpiderfy;

  // Handler do botao "Recentralizar": ajusta zoom/pan pra caber todos
  // os filteredMarkers visiveis no momento. Chama fitBounds direto no
  // mapRef (sem passar por state intermediario).
  const handleFitBounds = useCallback(() => {
    if (!google || !google.maps || !mapRef.current || !filteredMarkers.length) {
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    filteredMarkers.forEach((item) => {
      bounds.extend({ lat: item.lat, lng: item.lng });
    });
    mapRef.current.fitBounds(bounds, {
      top: 40,
      right: 120,
      bottom: 40,
      left: 120,
    });
  }, [google, filteredMarkers]);

  // Pad de navegacao manual (telao/TV): pan por pixels e zoom +/-.
  // panBy desloca o centro em px; passo fixo de 150px da um movimento
  // perceptivel sem exagero. Zoom respeita o minZoom (4) do mapa.
  const PAN_STEP = 150;
  const handlePan = useCallback((dx, dy) => {
    if (mapRef.current) mapRef.current.panBy(dx, dy);
  }, []);
  const handleZoom = useCallback((delta) => {
    const map = mapRef.current;
    if (!map) return;
    const z = map.getZoom();
    if (typeof z === 'number') map.setZoom(z + delta);
  }, []);

  // Segurar = acao continua no pad (TV sem mouse: clicar 10x pra
  // atravessar o mapa era penoso). pointerdown dispara a acao e arma um
  // interval; soltar/sair desarma. O onClick fica APENAS pro teclado
  // (Enter/Espaco geram click com detail===0) — sem ele o botao perderia
  // acessibilidade; com guard de detail nao ha disparo duplo no mouse.
  const holdIntervalRef = useRef(null);
  const stopHold = useCallback(() => {
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }
  }, []);
  useEffect(() => stopHold, [stopHold]);
  const bindHold = useCallback(
    (action, intervalMs = 130) => ({
      onPointerDown: (e) => {
        // So botao principal/touch — evita pan preso em right-click.
        if (e.button != null && e.button !== 0) return;
        stopHold();
        action();
        holdIntervalRef.current = setInterval(action, intervalMs);
      },
      onPointerUp: stopHold,
      onPointerLeave: stopHold,
      onPointerCancel: stopHold,
      onClick: (e) => {
        // Teclado (Enter/Espaco): click sintetico tem detail === 0.
        if (e.detail === 0) action();
      },
    }),
    [stopHold],
  );

  // ResizeObserver no container do mapa — detecta quando o div muda
  // de tamanho (ex.: aba "Alertas" -> "Mapa" no mobile, onde o
  // MobileMapWrapper alterna entre height:0 e altura cheia). Sem isso,
  // o Google Maps fica preso nas dimensoes da inicializacao (zero) e
  // nao renderiza tiles nem ajusta bounds. Quando o container
  // "ressuscita" (era 0, voltou >0), dispara o resize do gmaps e
  // refita nos markers — mesma visao do desktop, com todos visiveis.
  useEffect(() => {
    if (!google || !google.maps || !mapRef.current) return undefined;
    const mapDiv = mapRef.current.getDiv && mapRef.current.getDiv();
    if (!mapDiv || typeof ResizeObserver === 'undefined') return undefined;
    let lastW = mapDiv.offsetWidth;
    let lastH = mapDiv.offsetHeight;
    const obs = new ResizeObserver(() => {
      const w = mapDiv.offsetWidth;
      const h = mapDiv.offsetHeight;
      if (w === lastW && h === lastH) return;
      const wasInvisible = lastW === 0 || lastH === 0;
      lastW = w;
      lastH = h;
      if (w > 0 && h > 0) {
        google.maps.event.trigger(mapRef.current, 'resize');
        if (wasInvisible) handleFitBounds();
      }
    });
    obs.observe(mapDiv);
    return () => obs.disconnect();
  }, [google, handleFitBounds]);

  // Toggle satelite/streets. Default e roadmap (vias) com o estilo
  // customizado do tema. Satelite usa hybrid (imagery + labels).
  const [mapTypeId, setMapTypeId] = useState('roadmap');

const handleToggleMapType = useCallback(() => {
    setMapTypeId((current) =>
      current === 'roadmap' ? 'hybrid' : 'roadmap',
    );
  }, []);
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setMapTypeId(mapTypeId);
  }, [mapTypeId]);

  // Pan-to-marker ao selecionar: o painel lateral direito ocupa ~252px
  // (240 + 12 margin), entao um marker selecionado na borda direita do
  // mapa fica escondido por baixo dele. Centralizamos o marker e
  // offsetamos -126px (~metade da largura do painel) pra ele ficar bem
  // no centro do espaco visivel restante. Animacao smooth do Google
  // Maps (panTo + panBy fazem easing automatico).
  useEffect(() => {
    if (!selectedMarkerId || !mapRef.current || !google) return;
    const marker = nativeMarkersRef.current.get(selectedMarkerId);
    if (!marker) return;
    const pos = marker.getPosition();
    if (!pos) return;
    mapRef.current.panTo(pos);
    mapRef.current.panBy(-126, 0);
  }, [selectedMarkerId, google]);

  // Cleanup no unmount: remove todos os markers nativos + destroi o
  // clusterer. Sem isso, deixa os markers fantasmas no objeto map do
  // Google Maps mesmo apos o componente sumir.
  useEffect(
    () => () => {
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current.setMap(null);
        clustererRef.current = null;
      }
      nativeMarkersRef.current.forEach((m) => m.setMap(null));
      nativeMarkersRef.current.clear();
      // Baloes (pill) sao markers proprios fora do clusterer — limpa aqui.
      badgeMarkersRef.current.forEach((m) => m.setMap(null));
      badgeMarkersRef.current.clear();
      // Leque de spiderfy (markers + linhas temporarios).
      if (clearSpiderfyRef.current) clearSpiderfyRef.current();
    },
    [],
  );

  // Navega ao clicar num alerta no overlay do mapa. Mesma logica do
  // "Ver detalhes" do MarkerDetailPanel (handleSeeDetails): parque com
  // 1 unica aplicacao pula a tela intermediaria de lista e vai direto
  // pro detalhe do equipamento — triagem mais rapida, o operador chega
  // na origem do alarme com 1 clique. Multiplas aplicacoes mantem o
  // fluxo de escolha na lista (/dashboard/customer).
  const handleAlertClick = (marker) => {
    // Dispatch sempre — alimenta parqueSelecionado/clienteSelecionado em
    // Redux pra breadcrumb + atalho "voltar pro parque" do sidemenu
    // funcionarem independente do destino.
    dispatch({
      type: 'SEGUNDO/SET_PARQUE_SELECIONADO',
      filterStatus,
      parqueSelecionado: marker,
      clienteSelecionado: marker.customer,
    });
    const appliances = (marker && marker.appliance) || [];
    if (appliances.length === 1) {
      // Mesma shape de state que aplicacoes-cliente.js empurra
      // ({ ...aplicacao, ...parqueSelecionado }) — aplicacao.js le o
      // history.location.state como produto.
      history.push('/dashboard/customer/plant', {
        ...appliances[0],
        ...marker,
      });
    } else {
      history.push('/dashboard/customer', { ...marker.customer });
    }
  };

  // Fit do mapa em 2 estagios pra evitar fitar nos markers ANTIGOS
  // quando o operador acabou de trocar contexto (zona/cliente):
  //
  // 1. Quando zona/cliente muda no menu, marca `pendingFit = true`
  //    mas NAO fita ainda (markers ainda sao os do contexto anterior,
  //    o fetch da nova zona ainda esta em andamento).
  // 2. Quando markers chegam (nova reference de array), o useEffect
  //    de markers ve `pending=true` + reference diferente de
  //    `lastFitted` e finalmente fita. Marca pending=false +
  //    atualiza lastFitted.
  //
  // Em polling refresh subsequente (mesmo contexto, dados novos),
  // pendingFit ja e false — markers update sem refitar, preservando
  // zoom/pan do operador.
  // Estagio 1: troca de contexto marca pendente (refs pendingFitRef +
  // lastFittedMarkersRef declarados no topo do componente pra evitar
  // TDZ nos handlers que precisam deles antes desta linha).
  useEffect(() => {
    pendingFitRef.current = true;
  }, [selectedZona.id, selectedClient.id]);

  // Estagio 2: markers atualizaram (nova reference) — se pendente, fita.
  // Inclui mapZoom na dep pra disparar quando o map terminar de carregar
  // (mapZoom vai de null pra numero em handleMapReady). Sem isso, o
  // effect rodava ANTES do map estar pronto (mapRef.current=null) e
  // nao re-rodava quando o map montava.
  useEffect(() => {
    if (!pendingFitRef.current) return;
    if (
      !mapRef.current ||
      !google ||
      !google.maps ||
      !markers.length ||
      mapZoom === null
    )
      return;
    // Skip se markers e o mesmo array fitado anteriormente. Acontece
    // logo apos a troca de contexto (effect 1 ja rodou, mas o fetch
    // de markers ainda nao respondeu — markers ainda sao do contexto
    // antigo). Esperamos o setMarkers do novo fetch.
    if (lastFittedMarkersRef.current === markers) return;

    const bounds = new google.maps.LatLngBounds();
    markers.forEach((item) => {
      // Aceita lat/lng como number OU string parseavel — backend pode
      // retornar coords como string (e.g. "-23.583") dependendo do
      // endpoint. parseFloat + Number.isFinite cobre os 2 casos.
      const lat =
        typeof item.lat === 'number' ? item.lat : parseFloat(item.lat);
      const lng =
        typeof item.lng === 'number' ? item.lng : parseFloat(item.lng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        bounds.extend({ lat, lng });
      }
    });
    if (bounds.isEmpty()) return;

    // Padding right maior pra nao cobrir com AlertOverlay/MarkerDetail
    // (252px de painel + folga).
    mapRef.current.fitBounds(bounds, {
      top: 60,
      right: 280,
      bottom: 60,
      left: 60,
    });
    // Cap zoom max em 13 ao trocar zona — evita street-level se markers
    // do estado estiverem todos numa cidade so. Em zona ampla
    // (RS, MG), fitBounds da zoom menor naturalmente.
    google.maps.event.addListenerOnce(mapRef.current, 'idle', () => {
      const z = mapRef.current?.getZoom();
      if (typeof z === 'number' && z > 13) {
        mapRef.current.setZoom(13);
      }
    });

    pendingFitRef.current = false;
    lastFittedMarkersRef.current = markers;
  }, [google, markers, mapZoom]);

  // Limpa painel de detalhe SO se o parque saiu da lista (zona mudou
  // ou polling removeu). No refresh comum, a selecao continua intacta
  // — operador nao perde contexto a cada tick.
  useEffect(() => {
    if (!selectedMarkerId) return;
    const stillExists = markers.some((m) => m.id === selectedMarkerId);
    if (!stillExists) setSelectedMarker({});
  }, [markers, selectedMarkerId]);

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setOptions({ styles: mapStyles[title] });
    }
  }, [title]);

  const handleMapReady = (_mapProps, map) => {
    mapRef.current = map;
    // O fit inicial nos markers e feito pelo useEffect de contexto
    // (initialFitRef) assim que markers chegam — nao precisa duplicar
    // aqui. handleMapReady so faz o setup basico do mapa.
    map.setOptions({ styles: mapStyles[title], minZoom: 4 });

    // Track zoom pra controlar badges + clusterer maxZoom. Captura
    // valor inicial + listener pra mudancas (pinch/double-click/etc).
    setMapZoom(map.getZoom());
    map.addListener('zoom_changed', () => {
      if (mountedRef.current) setMapZoom(map.getZoom());
    });
  };

  // ESC limpa a busca (fecha o dropdown). Listener gated em search.
  // IMPORTANT: declarado ANTES do early return abaixo (if !zonas.length)
  // pra obedecer rules-of-hooks — hooks devem rodar incondicionalmente.
  useEffect(() => {
    if (!search) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setSearch('');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [search]);

  if (!zonas.length) return <Loader />;

  // Counts por zona pra exibir ao lado do nome no ScrollList CCO.
  // Conta markers (pracas) cuja zona corresponde. Inicializa TODAS as
  // zonas com 0 antes de iterar markers — assim zonas sem praca aparecem
  // com "0" em vez de undefined (sem badge).
  //
  // Backend retorna a praca com `marker.state = "MG"` (sigla UF) e
  // `marker.customer.estado = "MG"`. A zona tem so { id, name, lat, lng }
  // onde name e o nome COMPLETO ("MINAS GERAIS"). Mapeamos UF → nome
  // completo e fazemos lookup case-insensitive em zonas[].name.
  // Tambem mantemos fallbacks pra outros shapes (caso a API mude).
  const UF_TO_ZONE_NAME = {
    AC: 'ACRE',
    AL: 'ALAGOAS',
    AP: 'AMAPÁ',
    AM: 'AMAZONAS',
    BA: 'BAHIA',
    CE: 'CEARÁ',
    DF: 'DISTRITO FEDERAL',
    ES: 'ESPÍRITO SANTO',
    GO: 'GOIÁS',
    MA: 'MARANHÃO',
    MT: 'MATO GROSSO',
    MS: 'MATO GROSSO DO SUL',
    MG: 'MINAS GERAIS',
    PA: 'PARÁ',
    PB: 'PARAÍBA',
    PR: 'PARANÁ',
    PE: 'PERNAMBUCO',
    PI: 'PIAUÍ',
    RJ: 'RIO DE JANEIRO',
    RN: 'RIO GRANDE DO NORTE',
    RS: 'RIO GRANDE DO SUL',
    RO: 'RONDÔNIA',
    RR: 'RORAIMA',
    SC: 'SANTA CATARINA',
    SP: 'SÃO PAULO',
    SE: 'SERGIPE',
    TO: 'TOCANTINS',
  };

  // Normaliza pra comparacao case-insensitive + remove acentos pra
  // tolerar "MARANHAO" vs "MARANHÃO" / "Paraná" vs "PARANA".
  // RegExp construido em runtime via string com escape Unicode pra
  // garantir que o caractere range U+0300..U+036F (combining
  // diacritical marks) sobreviva a copy/paste e encoding de editor.
  const DIACRITIC_RE = new RegExp('[\\u0300-\\u036f]', 'g');
  const normalize = (s) =>
    (s || '')
      .toString()
      .normalize('NFD')
      .replace(DIACRITIC_RE, '')
      .toUpperCase()
      .trim();

  // Zona vem com prefixo no name: "BRASIL - Paraná" (e nao "PARANÁ"
  // puro). Match exato falha — entao fazemos .includes do nome do
  // estado normalizado dentro do zona.name normalizado.
  const matchZonaByStateName = (stateName) => {
    if (!stateName) return null;
    const target = normalize(stateName);
    return (zonas || []).find((zo) => {
      if (!zo) return false;
      const zname = normalize(zo.name);
      return zname.includes(target);
    });
  };

  const resolveZoneId = (marker) => {
    if (!marker) return null;
    // 1. zone object direto no parque (shape antigo, se um dia voltar)
    if (
      marker.zone &&
      typeof marker.zone === 'object' &&
      marker.zone.id != null
    ) {
      return marker.zone.id;
    }
    // 2. marker.state (UF) → mapeia pra nome completo → match em zonas[]
    const uf =
      (typeof marker.state === 'string' && marker.state) ||
      (marker.customer && marker.customer.estado) ||
      (marker.customer && marker.customer.state);
    if (uf) {
      const fullName = UF_TO_ZONE_NAME[uf.toUpperCase()] || uf;
      const z = matchZonaByStateName(fullName);
      if (z) return z.id;
    }
    // 3. marker.zone como string (state name direto)
    if (typeof marker.zone === 'string') {
      const z = matchZonaByStateName(marker.zone);
      if (z) return z.id;
    }
    // 4. zoneId direto (fallback final)
    if (marker.zoneId != null) return marker.zoneId;
    if (marker.zone_id != null) return marker.zone_id;
    if (marker.customer) {
      if (marker.customer.zoneId != null) return marker.customer.zoneId;
      if (marker.customer.zone_id != null) return marker.customer.zone_id;
    }
    return null;
  };

  const zoneCounts = ccoLayout
    ? (() => {
        const counts = {};
        (zonas || []).forEach((z) => {
          if (z && z.id != null) counts[z.id] = 0;
        });
        (markers || []).forEach((marker) => {
          const zoneId = resolveZoneId(marker);
          if (zoneId != null) {
            counts[zoneId] = (counts[zoneId] || 0) + 1;
          }
        });
        return counts;
      })()
    : {};

  // Contagem de MAQUINAS por status (todos os markers visiveis) — alimenta
  // a contagem ao lado de cada item da legenda. Usa o mesmo breakdown
  // por-maquina dos markers-pizza, somado sobre os parques. Const simples
  // (nao hook) por ficar depois de early returns, igual ao zoneCounts acima.
  const legendStatusCounts = (() => {
    const total = {
      'alarme/bloqueio': 0,
      atenção: 0,
      rodando: 0,
      desligada: 0,
      'sem dados': 0,
    };
    (markers || []).forEach((m) => {
      const { counts } = getParkStatusCounts(m);
      Object.keys(total).forEach((k) => {
        total[k] += counts[k] || 0;
      });
    });
    return total;
  })();

  // Contagem de parques em ALERTA por zona — usado pro badge vermelho ao
  // lado do total. Operador olha pra lista e ve em qual estado tem fogo
  // sem precisar percorrer overlay de alertas. Considera 'em alerta' o
  // mesmo criterio do AlertOverlay: statusMap.isRangeWarning ja inclui
  // alarme (E>0) e atencao (range WH/WL) na composicao live.
  const isMarkerAlerting = (m) =>
    !!(m && m.statusMap && m.statusMap.isRangeWarning);

  const zoneAlertCounts = ccoLayout
    ? (() => {
        const counts = {};
        (markers || []).forEach((marker) => {
          if (!isMarkerAlerting(marker)) return;
          const zoneId = resolveZoneId(marker);
          if (zoneId != null) {
            counts[zoneId] = (counts[zoneId] || 0) + 1;
          }
        });
        return counts;
      })()
    : {};


  // Counts por cliente pra exibir ao lado do nome no ScrollList CCO de
  // clientes (quando uma zona esta selecionada). Conta markers cujo
  // customer.id corresponde. Filtrado implicitamente pela zona porque
  // `clientes` (lista do scroll) ja vem filtrada por selectedZona via
  // saga/reducer. Inicializa TODOS os clientes com 0 pra mostrar "0"
  // explicito em vez de campo vazio.
  const customerCounts = ccoLayout
    ? (() => {
        const counts = {};
        (clientes || []).forEach((c) => {
          if (c && c.id != null) counts[c.id] = 0;
        });
        (markers || []).forEach((marker) => {
          const customerId =
            (marker.customer && marker.customer.id) ||
            marker.customerId ||
            (marker.appliance &&
              marker.appliance.customer &&
              marker.appliance.customer.id);
          if (customerId != null && counts[customerId] != null) {
            counts[customerId] += 1;
          }
        });
        return counts;
      })()
    : {};

  // Contagem por TIPO de equipamento por cliente (UCHA / EBA) pra colorir
  // os pills no ScrollList (laranja UCHA / azul EBA). Conta appliances de
  // cada marker do cliente classificando por appliance.type.type.
  const customerTypeCounts = ccoLayout
    ? (() => {
        const out = {};
        (clientes || []).forEach((c) => {
          if (c && c.id != null) out[c.id] = { ucha: 0, eba: 0 };
        });
        (markers || []).forEach((marker) => {
          const customerId =
            (marker.customer && marker.customer.id) ||
            marker.customerId ||
            (marker.appliance &&
              marker.appliance.customer &&
              marker.appliance.customer.id);
          if (customerId == null || !out[customerId]) return;
          const apps = Array.isArray(marker.appliance) ? marker.appliance : [];
          apps.forEach((a) => {
            const t = a && a.type && a.type.type;
            if (t === 'ucha') out[customerId].ucha += 1;
            else if (t === 'eba' || t === 'bomba') out[customerId].eba += 1;
          });
        });
        return out;
      })()
    : {};

  // Contagem de parques em alerta por cliente — mesma logica do zoneAlert.
  const customerAlertCounts = ccoLayout
    ? (() => {
        const counts = {};
        (markers || []).forEach((marker) => {
          if (!isMarkerAlerting(marker)) return;
          const customerId =
            (marker.customer && marker.customer.id) ||
            marker.customerId ||
            (marker.appliance &&
              marker.appliance.customer &&
              marker.appliance.customer.id);
          if (customerId != null) {
            counts[customerId] = (counts[customerId] || 0) + 1;
          }
        });
        return counts;
      })()
    : {};

  // Em CCO, oculta estados/clientes sem nenhum parque com equipamento
  // ativo. zoneCounts/customerCounts contam apenas markers visiveis (que
  // ja vem filtrados por applianceAmount>=1 no fetch), entao count 0 =
  // nada a monitorar -> some da lista, igual ao mapa. So aplica quando ja
  // ha markers carregados, senao a lista inteira sumiria durante o load
  // inicial (todos os counts comecam em 0 ate os markers chegarem).
  const hasMarkersLoaded = (markers || []).length > 0;
  const visibleZonas = (
    ccoLayout && hasMarkersLoaded
      ? (zonasFiltradas || zonas || []).filter(
          (z) => (zoneCounts[z.id] || 0) >= 1
        )
      : zonasFiltradas || zonas || []
  )
    // Ordem alfabetica (locale pt-BR, tolerante a acentos) — o backend
    // devolve as zonas em ordem de cadastro, que parecia aleatoria.
    .slice()
    .sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', 'pt-BR', {
        sensitivity: 'base',
      })
    );
  const visibleClientes = (
    ccoLayout && hasMarkersLoaded
      ? (clientesFiltrados || clientes || []).filter(
          (c) => (customerCounts[c.id] || 0) >= 1
        )
      : clientesFiltrados || clientes || []
  )
    // Ordem alfabetica (pt-BR, tolerante a acentos) — mesma logica das
    // zonas; o backend devolve em ordem de cadastro.
    .slice()
    .sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', 'pt-BR', {
        sensitivity: 'base',
      })
    );

  // Resultados do autocomplete dropdown. Mostra top 5 de cada
  // categoria pra nao escalar visualmente em buscas amplas.
  const SEARCH_DROPDOWN_MAX_PER_GROUP = 5;
  const dropdownZones = Array.isArray(zonasFiltradas)
    ? zonasFiltradas.slice(0, SEARCH_DROPDOWN_MAX_PER_GROUP)
    : [];
  const dropdownClients = Array.isArray(clientesFiltrados)
    ? clientesFiltrados.slice(0, SEARCH_DROPDOWN_MAX_PER_GROUP)
    : [];

  // Parks no autocomplete: busca em markers por nome ou nome do cliente.
  // Operador que sabe o nome do parque ('PCH São Fidélis') pula a navegacao
  // Estado -> Cliente -> Marker e abre direto o detail panel. Normaliza
  // tirando acentos (mesma logica de zonas/clientes).
  const dropdownParks = (() => {
    if (!search || search.length < 2) return [];
    if (!Array.isArray(markers) || markers.length === 0) return [];
    const DIACRITIC_RE = new RegExp('[\\u0300-\\u036f]', 'g');
    const norm = (s) =>
      String(s || '')
        .normalize('NFD')
        .replace(DIACRITIC_RE, '')
        .toLowerCase();
    const q = norm(search);
    if (!q) return [];
    const matches = [];
    for (const m of markers) {
      if (matches.length >= SEARCH_DROPDOWN_MAX_PER_GROUP) break;
      const nameHit = norm(m.name).includes(q);
      const customerHit =
        m.customer && m.customer.name && norm(m.customer.name).includes(q);
      if (nameHit || customerHit) matches.push(m);
    }
    return matches;
  })();

  const showSearchDropdown =
    search.length > 0 &&
    (dropdownZones.length > 0 ||
      dropdownClients.length > 0 ||
      dropdownParks.length > 0 ||
      search.length >= 2);

  // Click num resultado do dropdown — replica a logica dos ScrollList
  // existentes (que ja existem na sidebar abaixo). Limpa o campo de
  // search e fecha o dropdown.
  const handleSearchZoneClick = (zona) => {
    setSelectedClient({});
    setClientes([]);
    setSearch('');
    if (selectedZona.id === zona.id) {
      setSelectedZona({});
    } else {
      setSelectedZona(zona);
    }
  };
  const handleSearchClientClick = (cliente) => {
    dispatch({
      type: 'SEGUNDO/SET_PARQUE_SELECIONADO',
      filterStatus,
      clienteSelecionado: cliente,
      parqueSelecionado: {},
    });
    history.push('/dashboard/customer', { ...cliente });
  };

  // Click num parque do dropdown: limpa o search, abre o detail panel
  // (mesmo fluxo do handleMarkerClick) e pan suave do mapa pra posicao.
  const handleSearchParkClick = (park) => {
    setSearch('');
    setSelectedMarker({
      props: { position: park },
      name: park.name,
    });
    if (mapRef.current && park.lat != null && park.lng != null) {
      mapRef.current.panTo({ lat: park.lat, lng: park.lng });
      // Zoom mais proximo so se estiver muito longe; respeitar contexto
      // que o operador ja configurou.
      const z = mapRef.current.getZoom();
      if (z != null && z < 9) mapRef.current.setZoom(11);
    }
  };

  return (
    <s.Container $embedded={embedded} $cco={ccoLayout}>
      {/* Sidebar de zonas/clientes — escondida no modo embedded
          (ex.: /dashboard/customer onde o operador ja selecionou um
          cliente especifico e nao precisa navegar). */}
      {!embedded && (
      <s.ListsWrapper $cco={ccoLayout}>
        {ccoLayout ? (
          <s.CcoSearchBar>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              maxLength={30}
              placeholder="Pesquisar zona, cliente ou parque"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </s.CcoSearchBar>
        ) : (
          <Input
            maxLength={30}
            size="15px"
            noPadding
            src={icons.SearchIcon}
            placeholder="Pesquisar Zona ou Cliente"
            width="375px"
            height="35px"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
        )}
        {showSearchDropdown && (
          <s.SearchDropdown role="listbox">
            {dropdownZones.length > 0 && (
              <>
                <s.SearchDropdownGroupLabel>Zonas</s.SearchDropdownGroupLabel>
                {dropdownZones.map((zona) => (
                  <s.SearchDropdownItem
                    key={`sd-zone-${zona.id}`}
                    type="button"
                    onClick={() => handleSearchZoneClick(zona)}
                  >
                    <span className="item-icon">
                      {/* lucide:map-pin */}
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                    </span>
                    <span className="item-name">{zona.name}</span>
                  </s.SearchDropdownItem>
                ))}
              </>
            )}
            {dropdownClients.length > 0 && (
              <>
                <s.SearchDropdownGroupLabel>Clientes</s.SearchDropdownGroupLabel>
                {dropdownClients.map((cliente) => (
                  <s.SearchDropdownItem
                    key={`sd-client-${cliente.id}`}
                    type="button"
                    onClick={() => handleSearchClientClick(cliente)}
                  >
                    <span className="item-icon">
                      {/* lucide:building */}
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <rect x="4" y="2" width="16" height="20" rx="2" />
                        <path d="M9 22v-4h6v4" />
                        <path d="M8 6h.01 M12 6h.01 M16 6h.01 M8 10h.01 M12 10h.01 M16 10h.01 M8 14h.01 M12 14h.01 M16 14h.01" />
                      </svg>
                    </span>
                    <span className="item-name">{cliente.name}</span>
                  </s.SearchDropdownItem>
                ))}
              </>
            )}
            {dropdownParks.length > 0 && (
              <>
                <s.SearchDropdownGroupLabel>Parques</s.SearchDropdownGroupLabel>
                {dropdownParks.map((park) => (
                  <s.SearchDropdownItem
                    key={`sd-park-${park.id}`}
                    type="button"
                    onClick={() => handleSearchParkClick(park)}
                  >
                    <span className="item-icon">
                      {/* lucide:zap (turbina/geracao) */}
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                      </svg>
                    </span>
                    <span className="item-name">
                      {park.name}
                      {park.customer && park.customer.name && (
                        <span style={{ opacity: 0.55, marginLeft: 6 }}>
                          · {park.customer.name}
                        </span>
                      )}
                    </span>
                  </s.SearchDropdownItem>
                ))}
              </>
            )}
            {dropdownZones.length === 0 &&
              dropdownClients.length === 0 &&
              dropdownParks.length === 0 && (
                <s.SearchDropdownEmpty>
                  Nenhum resultado para “{search}”
                </s.SearchDropdownEmpty>
              )}
          </s.SearchDropdown>
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: ccoLayout ? 'column' : 'row',
            justifyContent: ccoLayout ? 'flex-start' : 'space-between',
            height: '100%',
            width: '100%',
            minHeight: 0,
          }}
        >
          {/* CCO drill-down: quando ha estado selecionado, mostra apenas
              a lista de clientes daquele estado com um botao "Voltar" no
              topo. Sem estado: mostra apenas a lista de estados.
              Layout legado (nao $cco) mostra os dois lado a lado. */}
          {(!ccoLayout || !selectedZona.id) && (
            <>
              {ccoLayout && showHoverHint && (
                <s.HoverPanHint role="status">
                  <span className="text">
                    Passe o mouse num estado para focar o mapa
                  </span>
                  <button
                    type="button"
                    onClick={dismissHoverHint}
                    aria-label="Dispensar dica"
                    title="Não mostrar de novo"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M18 6L6 18" />
                      <path d="M6 6l12 12" />
                    </svg>
                  </button>
                </s.HoverPanHint>
              )}
              <ScrollList
              type="zona"
              list={visibleZonas}
              filtered={zonasFiltradas}
              $cco={ccoLayout}
              counts={zoneCounts}
              alertCounts={zoneAlertCounts}
              onHoverItem={handleZonaHoverEnter}
              onHoverLeave={handleZonaHoverLeave}
              onClick={(zona) => {
                setSelectedClient({});
                setClientes([]);
                setSearch('');
                if (selectedZona.id === zona.id) {
                  setSelectedZona({});
                } else setSelectedZona(zona);
              }}
              selected={(zona) => zona.id === selectedZona.id}
              />
            </>
          )}
          {selectedZona.id && (
            <>
              {ccoLayout && (
                <s.CcoDrillBack
                  type="button"
                  onClick={() => {
                    setSelectedZona({});
                    setSelectedClient({});
                    setClientes([]);
                  }}
                  title={`Voltar para a lista de estados`}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="m15 6-6 6 6 6" />
                  </svg>
                  Voltar · {selectedZona.name}
                </s.CcoDrillBack>
              )}
              <ScrollList
                type="customer"
                list={visibleClientes}
                filtered={clientesFiltrados}
                $cco={ccoLayout}
                counts={customerCounts}
                typeCounts={customerTypeCounts}
                alertCounts={customerAlertCounts}
                onClick={(cliente) => {
                  dispatch({
                    type: 'SEGUNDO/SET_PARQUE_SELECIONADO',
                    filterStatus,
                    clienteSelecionado: cliente,
                    parqueSelecionado: {},
                  });
                  history.push('/dashboard/customer', { ...cliente });
                }}
                selected={(client) => client.id === selectedClient.id}
              />
            </>
          )}
        </div>
      </s.ListsWrapper>
      )}
      <s.ErrorMapContainer $embedded={embedded} $cco={ccoLayout}>
        <s.MapContainer id="dashboard-map-container">
          {!loadingMap && (
            <s.FullscreenButton
              type="button"
              onClick={toggleFullscreen}
              $centered={!embedded}
              title={
                isFullscreen
                  ? 'Sair da tela cheia (Esc)'
                  : 'Exibir mapa em tela cheia'
              }
              aria-label={
                isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'
              }
            >
              {isFullscreen ? (
                // Icone "comprimir": setas apontando pra dentro
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <polyline points="9 3 9 9 3 9" />
                  <polyline points="15 3 15 9 21 9" />
                  <polyline points="9 21 9 15 3 15" />
                  <polyline points="15 21 15 15 21 15" />
                </svg>
              ) : (
                // Icone "expandir": setas apontando pra fora
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <polyline points="3 9 3 3 9 3" />
                  <polyline points="21 9 21 3 15 3" />
                  <polyline points="3 15 3 21 9 21" />
                  <polyline points="21 15 21 21 15 21" />
                </svg>
              )}
              <span>{isFullscreen ? 'Sair' : 'Tela cheia'}</span>
            </s.FullscreenButton>
          )}
          {!loadingMap && (
            <s.UpdatedAgo
              $fs={isFullscreen}
              title={
                subscriptionHealth === 'failed'
                  ? 'Subscription GraphQL desconectada — aguardando reconexão'
                  : lastUpdate
                  ? `Última atualização: ${new Date(
                      lastUpdate
                    ).toLocaleTimeString('pt-BR')}`
                  : 'Aguardando primeiro ciclo de polling'
              }
              $failed={subscriptionHealth === 'failed'}
            >
              <s.PulseDot
                $stale={updatedAgoInfo.stale}
                $failed={subscriptionHealth === 'failed'}
              />
              <span>
                {subscriptionHealth === 'failed'
                  ? 'Reconectando…'
                  : `Atualizado ${updatedAgoInfo.label}`}
              </span>
            </s.UpdatedAgo>
          )}
          {!loadingMap && (
            <s.FitBoundsButton
              type="button"
              onClick={handleFitBounds}
              $centered={!embedded}
              title="Recentralizar mapa em todos os parques visíveis"
              aria-label="Recentralizar mapa"
            >
              {/* lucide:maximize — 4 setas saindo do centro pra cantos */}
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
              <span>Centralizar</span>
            </s.FitBoundsButton>
          )}
          {!loadingMap && (
            <s.MapStyleButton
              type="button"
              onClick={handleToggleMapType}
              $centered={!embedded}
              $active={mapTypeId === 'hybrid'}
              title={
                mapTypeId === 'hybrid'
                  ? 'Voltar pro mapa em vias'
                  : 'Alternar pra visão satélite'
              }
              aria-label="Alternar estilo do mapa"
              aria-pressed={mapTypeId === 'hybrid'}
            >
              {/* lucide:globe — visual de mapa-mundi */}
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              <span>{mapTypeId === 'hybrid' ? 'Vias' : 'Satélite'}</span>
            </s.MapStyleButton>
          )}
          {!loadingMap && (
            <s.BookmarksButton
              type="button"
              onClick={() => setBookmarksOpen((o) => !o)}
              $centered={!embedded}
              $active={bookmarksOpen}
              title="Visões salvas (bookmarks)"
              aria-haspopup="menu"
              aria-expanded={bookmarksOpen}
            >
              {/* lucide:bookmark */}
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
              </svg>
              <span>Visões</span>
            </s.BookmarksButton>
          )}
          {!loadingMap && bookmarksOpen && (
            <s.BookmarksDropdown role="menu" $centered={!embedded}>
              <s.BookmarksSaveButton
                type="button"
                onClick={handleSaveCurrentView}
              >
                {/* lucide:plus */}
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Salvar visão atual
              </s.BookmarksSaveButton>
              {savedViews.length === 0 ? (
                <s.BookmarksEmpty>
                  Sem visões salvas ainda. Pana/zoom o mapa e clique
                  acima pra salvar.
                </s.BookmarksEmpty>
              ) : (
                savedViews.map((view) => (
                  <s.BookmarkItem key={view.id}>
                    <s.BookmarkApplyButton
                      type="button"
                      onClick={() => handleApplyView(view)}
                      title={`Ir para "${view.name}" (zoom ${view.zoom})`}
                    >
                      {/* lucide:map-pin */}
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      <span className="name">{view.name}</span>
                    </s.BookmarkApplyButton>
                    <s.BookmarkDeleteButton
                      type="button"
                      onClick={() => handleDeleteView(view.id)}
                      title="Remover visão"
                      aria-label={`Remover ${view.name}`}
                    >
                      {/* lucide:trash-2 */}
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6 M14 11v6" />
                        <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                      </svg>
                    </s.BookmarkDeleteButton>
                  </s.BookmarkItem>
                ))
              )}
            </s.BookmarksDropdown>
          )}
          {!loadingMap && (
            <s.SoundToggleButton
              type="button"
              onClick={toggleSound}
              $centered={!embedded}
              $active={soundEnabled}
              title={
                soundEnabled
                  ? 'Som de alarme ativo — clique pra silenciar'
                  : 'Som de alarme desligado — clique pra ativar'
              }
              aria-label="Alternar som de alarme"
              aria-pressed={soundEnabled}
            >
              {soundEnabled ? (
                // lucide:volume-2 (som ativo)
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                </svg>
              ) : (
                // lucide:volume-x (som mudo)
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <line x1="23" y1="9" x2="17" y2="15" />
                  <line x1="17" y1="9" x2="23" y2="15" />
                </svg>
              )}
              <span>{soundEnabled ? 'Som on' : 'Som off'}</span>
            </s.SoundToggleButton>
          )}
          {!loadingMap && llmEnabled && (
            <s.ShiftSummaryButton
              type="button"
              onClick={() => setShiftSummaryOpen(true)}
              $centered={!embedded}
              title="Resumo do turno (IA) — briefing das últimas horas"
              aria-label="Resumo do turno"
            >
              {/* lucide:sparkles */}
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 3l1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3L12 3z" />
              </svg>
              <span>Resumo do turno</span>
            </s.ShiftSummaryButton>
          )}
          {shiftSummaryOpen && (
            <ShiftSummaryModal
              isOpen={shiftSummaryOpen}
              initialQuestion={shiftSummaryQuestion}
              onClose={() => {
                setShiftSummaryOpen(false);
                setShiftSummaryQuestion(null);
              }}
            />
          )}
          {!loadingMap && clusterPopup && (
            <s.ClusterPopup role="dialog" aria-label="Parques no cluster">
              <s.ClusterPopupHeader>
                <span>
                  {clusterPopup.parqueIds.length} parques nesta região
                </span>
                <button
                  type="button"
                  onClick={() => setClusterPopup(null)}
                  aria-label="Fechar"
                >
                  <svg viewBox="0 0 24 24">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </s.ClusterPopupHeader>
              <s.ClusterPopupList>
                {clusterPopup.parqueIds.map((parqueId) => {
                  const parque = markers.find((m) => m.id === parqueId);
                  if (!parque) return null;
                  const statusLabel = getStatus(
                    parque.statusMap?.status,
                    parque.statusMap?.isRangeWarning,
                  );
                  const color = getMarkerFillColor(statusLabel);
                  return (
                    <s.ClusterPopupItem
                      key={`cluster-item-${parqueId}`}
                      type="button"
                      color={color}
                      onClick={() => {
                        // Seleciona como se clicasse no marker
                        setSelectedMarker({
                          props: { position: parque },
                          name: parque.name,
                        });
                        setClusterPopup(null);
                      }}
                      title={`${parque.name} — ${statusLabel}`}
                    >
                      <span className="dot" />
                      <span className="name">{parque.name}</span>
                      {parque.customer?.name && (
                        <span className="customer">
                          {parque.customer.name}
                        </span>
                      )}
                    </s.ClusterPopupItem>
                  );
                })}
              </s.ClusterPopupList>
            </s.ClusterPopup>
          )}
          {/* FilterChipsBar removida — funcao migrada pra MapLegend
              clicavel no canto inferior esquerdo. Uma fonte unica de
              controle de filtro evita confusao. */}
          {loadingMap ? (
            <Loader color={colors.darkerblue} />
          ) : (
            <Map
              styles={mapStyles[title]}
              google={google}
              onReady={handleMapReady}
              visible
              // NAO passar prop 'center': google-maps-react chama
              // map.panTo(center) toda vez que o objeto muda referencia.
              // Como o objeto era re-criado a cada render do PracasMapa,
              // qualquer setCenter manual (cluster click etc) era
              // imediatamente revertido pra markers[0] (sempre o
              // mesmo: primeiro parque, que calhou ser em Juiz de Fora).
              // Mantemos so initialCenter (aplicado uma vez no mount).
              initialCenter={
                markers.length
                  ? { lat: markers[0].lat, lng: markers[0].lng }
                  : { lat: -14.235004, lng: -51.92528 }
              }
              containerStyle={{
                width: '100%',
                height: '100%',
                position: 'relative',
              }}
              zoomControl={false}
              mapTypeControl={false}
              scaleControl={false}
              streetViewControl={false}
              panControl={false}
              rotateControl={false}
              fullscreenControl={false}
              // gestureHandling 'greedy': scroll/touch panam sem precisar
              // Ctrl. Essencial pra TV touch-screen do CCO. Default era
              // 'auto' que exigia modifier em telas pequenas.
              gestureHandling="greedy"
              // maxZoom 17 cobre street-level (ver entradas individuais
              // de barragens). Sem limit, Google Maps zoom infinito ate
              // 22 — desperdicio de tiles + zero ganho operacional pra
              // CCO.
              maxZoom={17}
            >
              {/* Markers + clusters sao gerenciados imperativamente via
                  google.maps.Marker + MarkerClusterer num useEffect
                  abaixo. Antes usavamos <Marker> de google-maps-react,
                  mas a lib nao expoe a instancia google.maps.Marker
                  pro clusterer agrupar. Map JSX fica vazia (so a base
                  do mapa). */}
            </Map>
          )}
          {/* Aviso proeminente de tempo real interrompido. So aparece
              apos 6s de subscription 'failed' (debounce em
              showSubFailedBanner) pra nao piscar em reconexoes rapidas. */}
          {!loadingMap && showSubFailedBanner && (
            <s.SubscriptionBanner role="alert" aria-live="assertive">
              {/* lucide:wifi-off */}
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M1 1l22 22" />
                <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
                <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
                <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
                <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
                <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                <line x1="12" y1="20" x2="12.01" y2="20" />
              </svg>
              <span className="msg">
                Tempo real interrompido
                <span className="sub">
                  Dados podem estar desatualizados · reconectando…
                </span>
              </span>
              <s.SubscriptionReloadBtn
                type="button"
                onClick={() => window.location.reload()}
                title="Recarregar a pagina pra restabelecer a conexao"
              >
                {/* lucide:refresh-cw */}
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
                  <path d="M21 3v5h-5" />
                </svg>
                Recarregar
              </s.SubscriptionReloadBtn>
            </s.SubscriptionBanner>
          )}
          {/* Empty state: sem parques pra mostrar. Distingue 2 casos —
              sem dados de todo vs filtro escondeu tudo (com acao de
              limpar). Embedded (/cliente) nao mostra: la o contexto ja
              garante que ha equipamentos. */}
          {!loadingMap && !embedded && filteredMarkers.length === 0 && (
            <s.MapEmptyOverlay>
              <s.MapEmptyCard>
                {markers.length === 0 ? (
                  <EmptyState
                    title="Nenhum parque com dados"
                    description="Nenhuma estação retornou dados para exibir no mapa. Verifique a conexão ou aguarde o próximo ciclo de atualização."
                  />
                ) : (
                  <EmptyState
                    title="Nenhum parque com o filtro atual"
                    description={`${markers.length} ${
                      markers.length === 1
                        ? 'parque está oculto'
                        : 'parques estão ocultos'
                    } pelos filtros de status ativos.`}
                    action={
                      <s.EmptyFilterBtn type="button" onClick={resetFilters}>
                        Mostrar todos
                      </s.EmptyFilterBtn>
                    }
                  />
                )}
              </s.MapEmptyCard>
            </s.MapEmptyOverlay>
          )}
          {/* Legenda escondida em modo embedded (/dashboard/customer).
              No /cliente o operador ja sabe quais status existem nas
              suas maquinas (cards de UCHAs/EBAs mostram breakdown) —
              legenda adicional vira ruido visual sobre o mapa. */}
          {/* Em TELA CHEIA a legenda some daqui — vira o rodape compacto do
              dock "Top Geracao" na esquerda (substitui a legenda solta). */}
          {!loadingMap && !embedded && !isFullscreen && (
            <MapLegend
              activeFilters={filterStatus}
              onToggle={toggleFilter}
              counts={legendStatusCounts}
            />
          )}
          {/* Dock "TOP GERACAO" (lateral esquerda) — so TELA CHEIA/ccoLayout.
              Ranking das maquinas que mais geram + legenda compacta no pe. */}
          {!loadingMap && !embedded && ccoLayout && isFullscreen && (
            <s.FullscreenTopGenDock>
              <FullscreenTopGen
                activeFilters={filterStatus}
                onToggle={toggleFilter}
              />
            </s.FullscreenTopGenDock>
          )}
          {/* Pad de navegacao manual (pan + zoom) — so tela cheia (TV). */}
          {!loadingMap && !embedded && ccoLayout && isFullscreen && (
            <s.MapNavPad>
              <s.NavCross>
                <s.NavBtn
                  type="button"
                  data-dir="up"
                  {...bindHold(() => handlePan(0, -PAN_STEP))}
                  title="Mover para cima"
                  aria-label="Mover para cima"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="m18 15-6-6-6 6" />
                  </svg>
                </s.NavBtn>
                <s.NavBtn
                  type="button"
                  data-dir="left"
                  {...bindHold(() => handlePan(-PAN_STEP, 0))}
                  title="Mover para a esquerda"
                  aria-label="Mover para a esquerda"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </s.NavBtn>
                <s.NavBtn
                  type="button"
                  data-dir="right"
                  {...bindHold(() => handlePan(PAN_STEP, 0))}
                  title="Mover para a direita"
                  aria-label="Mover para a direita"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </s.NavBtn>
                <s.NavBtn
                  type="button"
                  data-dir="down"
                  {...bindHold(() => handlePan(0, PAN_STEP))}
                  title="Mover para baixo"
                  aria-label="Mover para baixo"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </s.NavBtn>
              </s.NavCross>
              <s.NavZoom>
                <s.NavBtn
                  type="button"
                  {...bindHold(() => handleZoom(1), 300)}
                  title="Aproximar (zoom +)"
                  aria-label="Aproximar"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </s.NavBtn>
                <s.NavBtn
                  type="button"
                  {...bindHold(() => handleZoom(-1), 300)}
                  title="Afastar (zoom −)"
                  aria-label="Afastar"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </s.NavBtn>
              </s.NavZoom>
            </s.MapNavPad>
          )}
          {/* Dock de alertas/atividade em TELA CHEIA (ccoLayout): a coluna 3
              do grid fica fora do #dashboard-map-container e some no
              fullscreen — aqui re-montamos o painel como card flutuante na
              lateral direita, dentro do elemento que entra em fullscreen. */}
          {!loadingMap && !embedded && ccoLayout && isFullscreen && (
            <s.FullscreenAlertDock>
              {selectedMarker.props ? (
                <MarkerDetailPanel
                  marker={selectedMarker.props}
                  alertCount={alertCount}
                  filterStatus={filterStatus}
                  onClose={closeDetailPanel}
                  history={
                    realtimeHistory[selectedMarker.props.position.id] || []
                  }
                  $cco
                  glass
                />
              ) : (
                <AlertOverlay
                  markers={markers}
                  onAlertClick={handleAlertClick}
                  freshAlarms={freshAlarms}
                  ackedAlerts={ackedAlerts}
                  onAckAlert={handleAckAlert}
                  activity={activity}
                  history={activityHistory}
                  onClearHistory={clearActivityHistory}
                onViewAllNotifications={() => history.push('/notifications')}
                onHoverAlert={setHoveredParqueId}
                hoveredId={hoveredParqueId}
                onAskAi={llmEnabled ? handleAskAi : undefined}
                  $cco
                  glass
                />
              )}
            </s.FullscreenAlertDock>
          )}
          {/* Totais "AGORA" (geracao/consumo + vazao) flutuando no rodape-
              central do mapa SO em tela cheia — visao de relance pra TV CCO. */}
          {!loadingMap && !embedded && ccoLayout && isFullscreen && (
            <FullscreenLiveTotals
              uchas={uchasOverview}
              ebas={ebasOverview}
            />
          )}
          {/* MarkerDetailPanel/AlertOverlay aqui SO quando NAO ccoLayout —
              em ccoLayout viram coluna 3 do grid (siblings do Container). */}
          {!loadingMap && !embedded && !ccoLayout &&
            (selectedMarker.props ? (
              <MarkerDetailPanel
                marker={selectedMarker.props}
                alertCount={alertCount}
                filterStatus={filterStatus}
                onClose={closeDetailPanel}
                history={
                  realtimeHistory[selectedMarker.props.position.id] || []
                }
              />
            ) : (
              <AlertOverlay
                markers={markers}
                onAlertClick={handleAlertClick}
                freshAlarms={freshAlarms}
                ackedAlerts={ackedAlerts}
                onAckAlert={handleAckAlert}
                activity={activity}
                history={activityHistory}
                onClearHistory={clearActivityHistory}
                onViewAllNotifications={() => history.push('/notifications')}
                onHoverAlert={setHoveredParqueId}
                hoveredId={hoveredParqueId}
                onAskAi={llmEnabled ? handleAskAi : undefined}
              />
            ))}
        </s.MapContainer>
      </s.ErrorMapContainer>

      {/* CCO layout: AlertOverlay/MarkerDetailPanel viram coluna 3
          (sibling do ErrorMapContainer, no grid do Container). */}
      {!loadingMap && !embedded && ccoLayout &&
        (selectedMarker.props ? (
          <MarkerDetailPanel
            marker={selectedMarker.props}
            alertCount={alertCount}
            filterStatus={filterStatus}
            onClose={closeDetailPanel}
            history={
              realtimeHistory[selectedMarker.props.position.id] || []
            }
            $cco
          />
        ) : (
          <AlertOverlay
            markers={markers}
            onAlertClick={handleAlertClick}
            freshAlarms={freshAlarms}
            ackedAlerts={ackedAlerts}
            onAckAlert={handleAckAlert}
            activity={activity}
            history={activityHistory}
            onClearHistory={clearActivityHistory}
            onViewAllNotifications={() => history.push('/notifications')}
            $cco
          />
        ))}
    </s.Container>
  );
};

const PracasMapaWithApi = GoogleApiWrapper({
  language: 'pt-BR',
  apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
})(PracasMapa);

// Wrappa em ErrorBoundary pra um crash no PracasMapa nao tomar o
// dashboard inteiro junto. Fallback amigavel com botao retry/reload.
const PracasMapaSafe = (props) => (
  <MapErrorBoundary>
    <PracasMapaWithApi {...props} />
  </MapErrorBoundary>
);

export default PracasMapaSafe;
